// =============================================================================
// AdminDashboard.js  —  /admin
// =============================================================================
// Full tenant admin interface. Each tab is a self-contained component defined
// at module scope to avoid remount on parent state changes.
//
// TABLE OF CONTENTS
//   ~17   OverviewTab          — stats cards, members-by-type, recent signups
//   ~87   MembersTab           — filterable member list
//   ~262  MemberDetail         — inline member view: profile, payments, status
//   ~399  MembershipTypesTab   — membership type CRUD
//   ~626  RenewalsTab          — renewal pipeline + bulk email + CSV export
//   ~988  LapsedTab            — win-back campaign: lapsed members + prospects
//   ~1230 EmailsTab            — per-tenant email template editor + test send
//   ~1380 SettingsTab          — org profile + renewal timing settings
//   ~1435 AdminSettingsTab     — admin/viewer account management
//         AdminActionsTab      — one-off admin jobs: Add a member (manual enrolment)
//   ~1640 StatCard             — shared stat card component
//   ~1649 StatusBadge          — coloured member status pill
//   ~1658 AdminDashboard       — root component, tab routing, navigation
// =============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

function apiClient() {
  const token = localStorage.getItem('token');
  return axios.create({ headers: { Authorization: `Bearer ${token}` } });
}

function getStoredUser() {
  try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; }
}

const STATUS_STYLES = {
  active:    { bg: '#dcfce7', color: '#15803d' },
  expiring:  { bg: '#fef9c3', color: '#854d0e' },
  lapsed:    { bg: '#fee2e2', color: '#dc2626' },
  cancelled: { bg: '#f1f5f9', color: '#64748b' }
};

// ── Overview Tab ─────────────────────────────────────────────────────────────
function OverviewTab() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient().get('/api/admin/stats')
      .then(res => setStats(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <p style={{ color: '#6b7280' }}>Loading stats...</p>;
  if (!stats) return <p style={{ color: 'var(--error)' }}>Failed to load stats.</p>;

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '2rem' }}>
        <StatCard label="Revenue This Month" value={`$${stats.revenueThisMonth.toFixed(2)}`} />
        <StatCard label="Expiring Soon (30d)" value={stats.expiringSoon} />
        <StatCard label="Member Types" value={stats.membersByType.length} />
      </div>

      {stats.membersByType.length > 0 && (
        <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: '12px', padding: '1.25rem', marginBottom: '1.5rem' }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1rem' }}>Members by Type</h3>
          {stats.membersByType.map(t => (
            <div key={t.name} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid var(--border)' }}>
              <span>{t.name || 'Unassigned'}</span>
              <span style={{ fontWeight: 600 }}>{t.count}</span>
            </div>
          ))}
        </div>
      )}

      <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: '12px', padding: '1.25rem' }}>
        <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1rem' }}>Recent Sign-ups</h3>
        {stats.recentSignups.length === 0 ? (
          <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>No members yet.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--border)' }}>
                <th style={{ textAlign: 'left', padding: '0.5rem 0.75rem', color: '#6b7280', fontWeight: 600 }}>Name</th>
                <th style={{ textAlign: 'left', padding: '0.5rem 0.75rem', color: '#6b7280', fontWeight: 600 }}>Email</th>
                <th style={{ textAlign: 'left', padding: '0.5rem 0.75rem', color: '#6b7280', fontWeight: 600 }}>Number</th>
                <th style={{ textAlign: 'left', padding: '0.5rem 0.75rem', color: '#6b7280', fontWeight: 600 }}>Status</th>
                <th style={{ textAlign: 'left', padding: '0.5rem 0.75rem', color: '#6b7280', fontWeight: 600 }}>Joined</th>
              </tr>
            </thead>
            <tbody>
              {stats.recentSignups.map(m => (
                <tr key={m.member_id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '0.75rem' }}>{m.full_name}</td>
                  <td style={{ padding: '0.75rem', color: '#6b7280' }}>{m.email}</td>
                  <td style={{ padding: '0.75rem', fontFamily: 'monospace', fontSize: '0.8rem' }}>{m.membership_number}</td>
                  <td style={{ padding: '0.75rem' }}>
                    <StatusBadge status={m.status} />
                  </td>
                  <td style={{ padding: '0.75rem', color: '#6b7280' }}>{new Date(m.created_at).toLocaleDateString('en-AU')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ── Members Tab ──────────────────────────────────────────────────────────────
function MembersTab() {
  const isViewer = getStoredUser().role === 'viewer';
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  // One-off message to a single member — the "Email" button on each row.
  const [contactTarget, setContactTarget] = useState(null);
  const [contactSubject, setContactSubject] = useState('');
  const [contactMessage, setContactMessage] = useState('');
  const [contactSending, setContactSending] = useState(false);
  const [contactError, setContactError] = useState('');
  const [contactSent, setContactSent] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [sort, setSort] = useState({ key: null, dir: 'asc' });

  const loadMembers = useCallback(() => {
    setLoading(true);
    const params = {};
    if (search) params.search = search;
    if (statusFilter) params.status = statusFilter;
    apiClient().get('/api/admin/members', { params })
      .then(res => setMembers(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [search, statusFilter]);

  useEffect(() => { loadMembers(); }, [loadMembers]);

  const updateStatus = async (memberId, status) => {
    try {
      await apiClient().put(`/api/admin/members/${memberId}/status`, { status });
      setMembers(ms => ms.map(m => m.member_id === memberId ? { ...m, status } : m));
      if (selected?.member_id === memberId) setSelected(s => ({ ...s, status }));
    } catch (e) {
      alert('Failed to update status.');
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await apiClient().delete(`/api/admin/members/${deleteTarget.member_id}`);
      setMembers(ms => ms.filter(m => m.member_id !== deleteTarget.member_id));
      setDeleteTarget(null);
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to delete member.');
    } finally {
      setDeleting(false);
    }
  };

  if (selected) {
    return (
      <MemberDetail
        member={selected}
        onBack={() => setSelected(null)}
        onStatusChange={(status) => updateStatus(selected.member_id, status)}
        onSaved={() => { setSelected(null); loadMembers(); }}
      />
    );
  }

  // Client-side sort by the clicked column; click again to toggle direction.
  const toggleSort = (key) => setSort(s =>
    s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });
  const sortArrow = (key) => sort.key === key
    ? <span style={{ color: '#0ea5e9', marginLeft: 4 }}>{sort.dir === 'asc' ? '▲' : '▼'}</span>
    : <span style={{ color: '#9ca3af', marginLeft: 4 }}>⇅</span>;

  const sortedMembers = (() => {
    if (!sort.key) return members;
    const val = (m) => {
      switch (sort.key) {
        case 'name':    return (m.full_name || '').toLowerCase();
        case 'type':    return (m.membership_type_name || '').toLowerCase();
        case 'expires': return m.expiry_date ? new Date(m.expiry_date).getTime() : Number.POSITIVE_INFINITY;
        case 'status':  return (m.status || '').toLowerCase();
        default:        return '';
      }
    };
    const arr = [...members].sort((a, b) => {
      const av = val(a), bv = val(b);
      return av < bv ? -1 : av > bv ? 1 : 0;
    });
    return sort.dir === 'asc' ? arr : arr.reverse();
  })();

  const thSort = (key) => ({ textAlign: 'left', padding: '0.75rem 1rem', color: sort.key === key ? '#374151' : '#6b7280', fontWeight: 600, cursor: 'pointer', userSelect: 'none' });

  const closeContact = () => {
    setContactTarget(null);
    setContactSubject('');
    setContactMessage('');
    setContactError('');
  };

  const sendContact = async () => {
    if (!contactMessage.trim()) return;
    setContactSending(true);
    setContactError('');
    try {
      const res = await apiClient().post(`/api/admin/members/${contactTarget.member_id}/contact`, {
        subject: contactSubject.trim(),
        message: contactMessage,
      });
      setContactSent(`Message sent to ${res.data.email}.`);
      closeContact();
      // Clear the confirmation after a moment; it is reassurance, not a record.
      setTimeout(() => setContactSent(''), 6000);
    } catch (err) {
      setContactError(err.response?.data?.error || 'Failed to send the message.');
    } finally {
      setContactSending(false);
    }
  };

  return (
    <div>
      {contactSent && (
        <div style={{ background: '#ecfdf5', border: '1px solid #a7f3d0', color: '#065f46', borderRadius: '8px', padding: '0.6rem 1rem', marginBottom: '1rem', fontSize: '0.875rem' }}>
          {contactSent}
        </div>
      )}

      {/* One-off message to a single member */}
      {contactTarget && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{
            background: '#fff', borderRadius: '12px', padding: '2rem',
            maxWidth: '520px', width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          }}>
            <h3 style={{ fontWeight: 700, marginBottom: '0.35rem', fontSize: '1.1rem' }}>Email Member</h3>
            <p style={{ color: '#6b7280', fontSize: '0.875rem', marginBottom: '1.25rem' }}>
              To <strong>{contactTarget.full_name || contactTarget.email}</strong> &lt;{contactTarget.email}&gt;
            </p>

            {contactError && (
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: '8px', padding: '0.5rem 0.75rem', marginBottom: '0.9rem', fontSize: '0.85rem' }}>
                {contactError}
              </div>
            )}

            <div className="form-group">
              <label>Subject <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
              <input
                value={contactSubject}
                onChange={(e) => setContactSubject(e.target.value)}
                placeholder="A message from SCALA"
              />
            </div>
            <div className="form-group">
              <label>Message</label>
              <textarea
                rows={7}
                value={contactMessage}
                onChange={(e) => setContactMessage(e.target.value)}
                placeholder="Write your message…"
                style={{ width: '100%', padding: '0.75rem 1rem', border: '1.5px solid var(--border)', borderRadius: '8px', fontSize: '0.875rem', fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }}
              />
            </div>

            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
              <button
                onClick={closeContact}
                disabled={contactSending}
                style={{ background: '#f1f5f9', color: '#374151', border: 'none', borderRadius: '8px', padding: '0.6rem 1.25rem', fontWeight: 600, cursor: 'pointer', fontSize: '0.875rem' }}
              >
                Cancel
              </button>
              <button
                onClick={sendContact}
                disabled={contactSending || !contactMessage.trim()}
                style={{ background: '#1a1a2e', color: '#fff', border: 'none', borderRadius: '8px', padding: '0.6rem 1.25rem', fontWeight: 600, cursor: 'pointer', fontSize: '0.875rem', opacity: (contactSending || !contactMessage.trim()) ? 0.6 : 1 }}
              >
                {contactSending ? 'Sending…' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {deleteTarget && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{
            background: '#fff', borderRadius: '12px', padding: '2rem',
            maxWidth: '420px', width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          }}>
            <h3 style={{ fontWeight: 700, marginBottom: '0.75rem', fontSize: '1.1rem' }}>Delete Member</h3>
            <p style={{ color: '#374151', marginBottom: '0.5rem' }}>
              Are you sure you want to permanently delete <strong>{deleteTarget.full_name}</strong>?
            </p>
            <p style={{ color: '#6b7280', fontSize: '0.875rem', marginBottom: '1.5rem' }}>
              This will remove their member record, user account, and all associated payments. This action cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
                style={{ background: '#f1f5f9', color: '#374151', border: 'none', borderRadius: '8px', padding: '0.6rem 1.25rem', fontWeight: 600, cursor: 'pointer', fontSize: '0.875rem' }}
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleting}
                style={{ background: '#ef4444', color: '#fff', border: 'none', borderRadius: '8px', padding: '0.6rem 1.25rem', fontWeight: 600, cursor: 'pointer', fontSize: '0.875rem', opacity: deleting ? 0.7 : 1 }}
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem' }}>
        <input
          type="search"
          placeholder="Search name or email..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ flex: 1, padding: '0.625rem 1rem', border: '1.5px solid var(--border)', borderRadius: '8px', fontSize: '0.875rem', outline: 'none' }}
        />
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          style={{ padding: '0.625rem 1rem', border: '1.5px solid var(--border)', borderRadius: '8px', fontSize: '0.875rem', outline: 'none' }}
        >
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="expiring">Expiring</option>
          <option value="lapsed">Lapsed</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      {loading ? (
        <p style={{ color: '#6b7280' }}>Loading members...</p>
      ) : members.length === 0 ? (
        <p style={{ color: '#6b7280' }}>No members found.</p>
      ) : (
        <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: '12px', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--border)', background: '#f9fafb' }}>
                <th onClick={() => toggleSort('name')}   style={thSort('name')}>Member{sortArrow('name')}</th>
                <th style={{ textAlign: 'left', padding: '0.75rem 1rem', color: '#6b7280', fontWeight: 600 }}>Number</th>
                <th onClick={() => toggleSort('type')}    style={thSort('type')}>Type{sortArrow('type')}</th>
                <th onClick={() => toggleSort('expires')} style={thSort('expires')}>Expires{sortArrow('expires')}</th>
                <th onClick={() => toggleSort('status')}  style={thSort('status')}>Status{sortArrow('status')}</th>
                <th style={{ padding: '0.75rem 1rem' }}></th>
              </tr>
            </thead>
            <tbody>
              {sortedMembers.map(m => (
                <tr key={m.member_id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '0.875rem 1rem' }}>
                    <div style={{ fontWeight: 600 }}>{m.full_name}</div>
                    <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>{m.email}</div>
                  </td>
                  <td style={{ padding: '0.875rem 1rem', fontFamily: 'monospace', fontSize: '0.8rem' }}>{m.membership_number}</td>
                  <td style={{ padding: '0.875rem 1rem' }}>{m.membership_type_name || '—'}</td>
                  <td style={{ padding: '0.875rem 1rem', color: '#6b7280' }}>
                    {m.expiry_date ? new Date(m.expiry_date).toLocaleDateString('en-AU') : '—'}
                  </td>
                  <td style={{ padding: '0.875rem 1rem' }}>
                    <StatusBadge status={m.status} />
                  </td>
                  <td style={{ padding: '0.875rem 1rem' }}>
                    <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                      <button
                        onClick={() => setSelected(m)}
                        style={{ background: 'none', border: '1px solid var(--border)', borderRadius: '6px', padding: '0.3rem 0.75rem', cursor: 'pointer', fontSize: '0.8rem' }}
                      >
                        View
                      </button>
                      {!isViewer && (
                        <button
                          onClick={() => setContactTarget(m)}
                          title={`Send a message to ${m.full_name || m.email}`}
                          style={{ background: 'none', border: '1px solid var(--border)', borderRadius: '6px', padding: '0.3rem 0.75rem', cursor: 'pointer', fontSize: '0.8rem' }}
                        >
                          Email
                        </button>
                      )}
                      {!isViewer && (
                        <button
                          onClick={() => setDeleteTarget(m)}
                          style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', borderRadius: '6px', padding: '0.3rem 0.75rem', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function MemberDetail({ member, onBack, onStatusChange, onSaved }) {
  const isViewer = getStoredUser().role === 'viewer';
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({});

  useEffect(() => {
    apiClient().get(`/api/admin/members/${member.member_id}`)
      .then(res => { setDetail(res.data); setForm({ full_name: res.data.full_name, phone: res.data.phone || '', address: res.data.address || '' }); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [member.member_id]);

  const saveEdit = async () => {
    try {
      await apiClient().put(`/api/admin/members/${member.member_id}`, form);
      setDetail(d => ({ ...d, ...form }));
      setEditMode(false);
    } catch { alert('Save failed.'); }
  };

  if (loading) return <p style={{ color: '#6b7280' }}>Loading...</p>;

  return (
    <div>
      <button onClick={onBack} style={{ background: 'none', border: 'none', color: 'var(--secondary)', cursor: 'pointer', fontWeight: 600, marginBottom: '1rem', padding: 0 }}>
        ← Back to members
      </button>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
        <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: '12px', padding: '1.25rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
            <h3 style={{ fontWeight: 600 }}>Member Details</h3>
            {!isViewer && (
              <button onClick={() => setEditMode(!editMode)} style={{ background: 'none', border: 'none', color: 'var(--secondary)', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem' }}>
                {editMode ? 'Cancel' : 'Edit'}
              </button>
            )}
          </div>
          {editMode ? (
            <>
              {['full_name','phone','address'].map(f => (
                <div key={f} className="form-group" style={{ marginBottom: '0.5rem' }}>
                  <label style={{ fontSize: '0.75rem', textTransform: 'capitalize' }}>{f.replace('_', ' ')}</label>
                  <input value={form[f]} onChange={e => setForm(p => ({ ...p, [f]: e.target.value }))} style={{ padding: '0.5rem 0.75rem', fontSize: '0.875rem' }} />
                </div>
              ))}
              <button onClick={saveEdit} className="btn btn-primary" style={{ marginTop: '0.5rem', padding: '0.5rem 1rem' }}>Save</button>
            </>
          ) : (
            <div style={{ fontSize: '0.875rem' }}>
              {[['Name', detail.full_name], ['Email', detail.email], ['Phone', detail.phone || '—'], ['Address', detail.address || '—']].map(([k, v]) => (
                <div key={k} style={{ marginBottom: '0.4rem' }}><strong>{k}:</strong> {v}</div>
              ))}
            </div>
          )}
        </div>

        <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: '12px', padding: '1.25rem' }}>
          <h3 style={{ fontWeight: 600, marginBottom: '0.75rem' }}>Membership</h3>
          <div style={{ fontSize: '0.875rem' }}>
            {[
              ['Number', detail.membership_number],
              ['Type', detail.membership_type_name || '—'],
              ['Status', null],
              ['Start', detail.start_date ? new Date(detail.start_date).toLocaleDateString('en-AU') : '—'],
              ['Expires', detail.expiry_date ? new Date(detail.expiry_date).toLocaleDateString('en-AU') : (detail.is_lifetime ? 'Lifetime' : '—')]
            ].map(([k, v]) => (
              <div key={k} style={{ marginBottom: '0.4rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <strong>{k}:</strong>
                {k === 'Status' ? <StatusBadge status={detail.status} /> : v}
              </div>
            ))}
          </div>
          {!isViewer && (
            <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {['active','expiring','lapsed','cancelled'].map(s => (
                <button
                  key={s}
                  onClick={() => onStatusChange(s)}
                  style={{
                    padding: '0.3rem 0.75rem', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer',
                    border: `1px solid ${STATUS_STYLES[s].color}`,
                    background: detail.status === s ? STATUS_STYLES[s].bg : 'transparent',
                    color: STATUS_STYLES[s].color
                  }}
                >
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {detail.payments?.length > 0 && (
        <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: '12px', padding: '1.25rem' }}>
          <h3 style={{ fontWeight: 600, marginBottom: '1rem' }}>Payment History</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--border)' }}>
                {['Date', 'Type', 'Amount', 'Status'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '0.5rem 0.75rem', color: '#6b7280', fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {detail.payments.map(p => (
                <tr key={p.payment_id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '0.75rem' }}>{new Date(p.payment_date).toLocaleDateString('en-AU')}</td>
                  <td style={{ padding: '0.75rem', textTransform: 'capitalize' }}>{p.payment_type}</td>
                  <td style={{ padding: '0.75rem', fontWeight: 600 }}>${parseFloat(p.amount).toFixed(2)}</td>
                  <td style={{ padding: '0.75rem' }}>
                    <span style={{ background: '#dcfce7', color: '#15803d', padding: '0.2rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem' }}>{p.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const DURATION_UNITS = [
  { value: 'days',   label: 'Days'   },
  { value: 'weeks',  label: 'Weeks'  },
  { value: 'months', label: 'Months' },
  { value: 'years',  label: 'Years'  },
];

function fmtDuration(value, unit) {
  if (!value) return 'Lifetime';
  const v = parseInt(value);
  const u = (unit || 'months').toLowerCase();
  const singular = { days: 'day', weeks: 'week', months: 'month', years: 'year' }[u] || u;
  return v === 1 ? `1 ${singular}` : `${v} ${singular}s`;
}

// ── Membership Types Tab ─────────────────────────────────────────────────────
function MembershipTypesTab() {
  const isViewer = getStoredUser().role === 'viewer';
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', fee: '0', duration_months: '12', duration_unit: 'months', is_public: true });
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);

  useEffect(() => {
    apiClient().get('/api/admin/membership-types')
      .then(res => setTypes(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await apiClient().post('/api/admin/membership-types', form);
      setTypes(ts => [...ts, res.data]);
      setShowForm(false);
      setForm({ name: '', description: '', fee: '0', duration_months: '12', duration_unit: 'months', is_public: true });
    } catch { alert('Failed to create type.'); }
    finally { setSaving(false); }
  };

  const startEdit = (t) => {
    setEditingId(t.type_id);
    setEditForm({ name: t.name, fee: String(t.fee), duration_months: String(t.duration_months || ''), duration_unit: t.duration_unit || 'months', is_active: t.is_active });
  };

  const cancelEdit = () => setEditingId(null);

  const handleEditSave = async (id) => {
    setEditSaving(true);
    try {
      const res = await apiClient().put(`/api/admin/membership-types/${id}`, {
        name: editForm.name,
        fee: editForm.fee,
        duration_months: editForm.duration_months || null,
        duration_unit: editForm.duration_unit || 'months',
        is_active: editForm.is_active,
      });
      setTypes(ts => ts.map(t => t.type_id === id ? { ...t, ...res.data } : t));
      setEditingId(null);
    } catch { alert('Failed to save changes.'); }
    finally { setEditSaving(false); }
  };

  const deactivate = async (id) => {
    if (!window.confirm('Deactivate this membership type?')) return;
    try {
      await apiClient().delete(`/api/admin/membership-types/${id}`);
      setTypes(ts => ts.map(t => t.type_id === id ? { ...t, is_active: false } : t));
    } catch { alert('Failed to deactivate.'); }
  };

  return (
    <div>
      {!isViewer && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
          <button onClick={() => setShowForm(!showForm)} className="btn btn-primary" style={{ width: 'auto', padding: '0.625rem 1.25rem' }}>
            {showForm ? 'Cancel' : '+ Add Type'}
          </button>
        </div>
      )}

      {showForm && (
        <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: '12px', padding: '1.25rem', marginBottom: '1.5rem' }}>
          <h3 style={{ fontWeight: 600, marginBottom: '1rem' }}>New Membership Type</h3>
          <form onSubmit={handleCreate}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              <div className="form-group"><label>Name</label><input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required /></div>
              <div className="form-group"><label>Annual Fee (AUD)</label><input type="number" min="0" step="0.01" value={form.fee} onChange={e => setForm(f => ({ ...f, fee: e.target.value }))} /></div>
              <div className="form-group">
                <label>Duration</label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <input
                    type="number" min="1"
                    value={form.duration_months}
                    onChange={e => setForm(f => ({ ...f, duration_months: e.target.value }))}
                    style={{ flex: '2' }}
                  />
                  <select
                    value={form.duration_unit}
                    onChange={e => setForm(f => ({ ...f, duration_unit: e.target.value }))}
                    style={{ flex: '1', padding: '0.75rem 0.5rem', border: '1.5px solid var(--border)', borderRadius: '8px', fontSize: '0.95rem' }}
                  >
                    {DURATION_UNITS.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>Visibility</label>
                <select value={form.is_public ? 'true' : 'false'} onChange={e => setForm(f => ({ ...f, is_public: e.target.value === 'true' }))} style={{ width: '100%', padding: '0.75rem 1rem', border: '1.5px solid var(--border)', borderRadius: '8px' }}>
                  <option value="true">Public</option>
                  <option value="false">Admin only</option>
                </select>
              </div>
              <div className="form-group" style={{ gridColumn: '1/-1' }}><label>Description</label><input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} /></div>
            </div>
            <button type="submit" className="btn btn-primary" disabled={saving} style={{ marginTop: '0.5rem', width: 'auto', padding: '0.625rem 1.5rem' }}>
              {saving ? 'Creating...' : 'Create Type'}
            </button>
          </form>
        </div>
      )}

      {loading ? <p style={{ color: '#6b7280' }}>Loading...</p> : (
        <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: '12px', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--border)', background: '#f9fafb' }}>
                {['Name', 'Fee', 'Duration', 'Visibility', 'Status', ''].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '0.75rem 1rem', color: '#6b7280', fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {types.map(t => editingId === t.type_id ? (
                <tr key={t.type_id} style={{ borderBottom: '1px solid var(--border)', background: '#f8f9ff' }}>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    <input
                      value={editForm.name}
                      onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))}
                      style={{ padding: '0.4rem 0.6rem', border: '1.5px solid var(--border)', borderRadius: '6px', fontSize: '0.875rem', width: '100%' }}
                    />
                  </td>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    <input
                      type="number" min="0" step="0.01"
                      value={editForm.fee}
                      onChange={e => setEditForm(f => ({ ...f, fee: e.target.value }))}
                      style={{ padding: '0.4rem 0.6rem', border: '1.5px solid var(--border)', borderRadius: '6px', fontSize: '0.875rem', width: '80px' }}
                    />
                  </td>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    <div style={{ display: 'flex', gap: '0.35rem', alignItems: 'center' }}>
                      <input
                        type="number" min="1"
                        value={editForm.duration_months}
                        onChange={e => setEditForm(f => ({ ...f, duration_months: e.target.value }))}
                        style={{ padding: '0.4rem 0.5rem', border: '1.5px solid var(--border)', borderRadius: '6px', fontSize: '0.875rem', width: '60px' }}
                      />
                      <select
                        value={editForm.duration_unit || 'months'}
                        onChange={e => setEditForm(f => ({ ...f, duration_unit: e.target.value }))}
                        style={{ padding: '0.4rem 0.4rem', border: '1.5px solid var(--border)', borderRadius: '6px', fontSize: '0.8rem' }}
                      >
                        {DURATION_UNITS.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
                      </select>
                    </div>
                  </td>
                  <td style={{ padding: '0.75rem 1rem', color: '#9ca3af', fontSize: '0.8rem' }}>—</td>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    <select
                      value={editForm.is_active ? 'true' : 'false'}
                      onChange={e => setEditForm(f => ({ ...f, is_active: e.target.value === 'true' }))}
                      style={{ padding: '0.35rem 0.5rem', border: '1.5px solid var(--border)', borderRadius: '6px', fontSize: '0.8rem' }}
                    >
                      <option value="true">Active</option>
                      <option value="false">Inactive</option>
                    </select>
                  </td>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    <div style={{ display: 'flex', gap: '0.4rem' }}>
                      <button
                        onClick={() => handleEditSave(t.type_id)}
                        disabled={editSaving}
                        style={{ background: '#1a1a2e', color: '#fff', border: 'none', borderRadius: '6px', padding: '0.3rem 0.75rem', cursor: editSaving ? 'not-allowed' : 'pointer', fontSize: '0.75rem', fontWeight: 600 }}
                      >
                        {editSaving ? '…' : 'Save'}
                      </button>
                      <button
                        onClick={cancelEdit}
                        style={{ background: '#f1f5f9', color: '#374151', border: 'none', borderRadius: '6px', padding: '0.3rem 0.75rem', cursor: 'pointer', fontSize: '0.75rem' }}
                      >
                        Cancel
                      </button>
                    </div>
                  </td>
                </tr>
              ) : (
                <tr key={t.type_id} style={{ borderBottom: '1px solid var(--border)', opacity: t.is_active ? 1 : 0.5 }}>
                  <td style={{ padding: '0.875rem 1rem' }}>
                    <div style={{ fontWeight: 600 }}>{t.name}</div>
                    {t.description && <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>{t.description}</div>}
                  </td>
                  <td style={{ padding: '0.875rem 1rem', fontWeight: 600 }}>${parseFloat(t.fee).toFixed(2)}</td>
                  <td style={{ padding: '0.875rem 1rem' }}>{fmtDuration(t.duration_months, t.duration_unit)}</td>
                  <td style={{ padding: '0.875rem 1rem' }}>
                    <span style={{ fontSize: '0.75rem', color: t.is_public ? '#15803d' : '#64748b' }}>
                      {t.is_public ? 'Public' : 'Admin only'}
                    </span>
                  </td>
                  <td style={{ padding: '0.875rem 1rem' }}>
                    <span style={{ background: t.is_active ? '#dcfce7' : '#f1f5f9', color: t.is_active ? '#15803d' : '#64748b', padding: '0.2rem 0.6rem', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: 600 }}>
                      {t.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={{ padding: '0.875rem 1rem' }}>
                    {!isViewer && (
                      <div style={{ display: 'flex', gap: '0.4rem' }}>
                        <button
                          onClick={() => startEdit(t)}
                          style={{ background: 'none', border: '1px solid var(--border)', borderRadius: '6px', padding: '0.3rem 0.75rem', cursor: 'pointer', fontSize: '0.75rem' }}
                        >
                          Edit
                        </button>
                        {t.is_active && (
                          <button onClick={() => deactivate(t.type_id)} style={{ background: 'none', border: '1px solid #fca5a5', borderRadius: '6px', padding: '0.3rem 0.75rem', cursor: 'pointer', fontSize: '0.75rem', color: '#dc2626' }}>
                            Deactivate
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Renewals Tab ─────────────────────────────────────────────────────────────
function RenewalsTab() {
  const isViewer = getStoredUser().role === 'viewer';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [search, setSearch] = useState('');
  const [bulkMsg, setBulkMsg] = useState('');
  const [bulkSending, setBulkSending] = useState(false);
  const [actionBusy, setActionBusy] = useState(null); // member_id for row-level actions
  const [selected, setSelected] = useState(new Set());
  const [expiryModal, setExpiryModal] = useState(null); // { memberId, current }
  const [expiryInput, setExpiryInput] = useState('');
  const [expiryMsg, setExpiryMsg] = useState('');
  const [remindFlash, setRemindFlash] = useState({}); // { [memberId]: 'Sent!' | 'Failed' }

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (filter) params.renewal_status = filter;
    if (search) params.search = search;
    apiClient().get('/api/admin/renewals', { params })
      .then(res => { setData(res.data); setSelected(new Set()); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [filter, search]);

  useEffect(() => { load(); }, [load]);

  const handleBulkEmail = async () => {
    const ids = selected.size > 0 ? [...selected] : null;
    const templateKey = filter === 'due' ? 'renewal_reminder_14' :
                        filter === 'overdue' ? 'renewal_grace_7' : 'renewal_lapsed';
    if (!ids && !filter) { alert('Select members or a status filter first.'); return; }
    const label = ids ? `${ids.length} selected member${ids.length !== 1 ? 's' : ''}` : `all ${filter} members`;
    if (!window.confirm(`Send reminder email to ${label}?`)) return;
    setBulkSending(true); setBulkMsg('');
    try {
      const payload = ids ? { member_ids: ids, template_key: templateKey }
                          : { renewal_status: filter, template_key: templateKey };
      const res = await apiClient().post('/api/admin/renewals/bulk-email', payload);
      setBulkMsg(`Sent ${res.data.sent} email${res.data.sent !== 1 ? 's' : ''}${res.data.failed > 0 ? `, ${res.data.failed} failed` : ''}.`);
      setSelected(new Set());
    } catch (err) {
      setBulkMsg(err.response?.data?.error || 'Failed to send emails.');
    } finally {
      setBulkSending(false);
    }
  };

  const handleManualRenew = async (memberId) => {
    if (!window.confirm('Mark this member as manually renewed? Their expiry will be extended by the configured duration.')) return;
    setActionBusy(memberId);
    try {
      await apiClient().post(`/api/admin/members/${memberId}/renew`);
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to renew member.');
    } finally {
      setActionBusy(null);
    }
  };

  const reminderTemplateKey = (status) => {
    if (status === 'overdue') return 'renewal_grace';
    if (status === 'lapsed')  return 'renewal_lapsed';
    return 'renewal_due';
  };

  const handleSendReminder = async (member) => {
    const templateKey = reminderTemplateKey(member.status);
    setActionBusy(member.member_id + '_remind');
    setRemindFlash(f => ({ ...f, [member.member_id]: null }));
    try {
      await apiClient().post(`/api/admin/members/${member.member_id}/send-reminder`, { template_key: templateKey });
      setRemindFlash(f => ({ ...f, [member.member_id]: 'Sent!' }));
      setTimeout(() => setRemindFlash(f => ({ ...f, [member.member_id]: null })), 2500);
    } catch (err) {
      setRemindFlash(f => ({ ...f, [member.member_id]: 'Failed' }));
      setTimeout(() => setRemindFlash(f => ({ ...f, [member.member_id]: null })), 3000);
    } finally {
      setActionBusy(null);
    }
  };

  const handleExpiryOverride = async () => {
    if (!expiryInput) return;
    setExpiryMsg('');
    try {
      await apiClient().put(`/api/admin/members/${expiryModal.memberId}/expiry`, { expiry_date: expiryInput });
      setExpiryModal(null);
      load();
    } catch (err) {
      setExpiryMsg(err.response?.data?.error || 'Failed to update expiry.');
    }
  };

  const handleExportCSV = () => {
    const params = new URLSearchParams();
    if (filter) params.set('renewal_status', filter);
    const token = localStorage.getItem('token');
    const url = `/api/admin/renewals/export?${params.toString()}`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = 'renewals.csv';
        a.click();
      });
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const ids = data?.members?.map(m => m.member_id) || [];
    setSelected(prev => prev.size === ids.length ? new Set() : new Set(ids));
  };

  const RENEWAL_STATUS_STYLES = {
    active:   { bg: '#dcfce7', color: '#15803d' },
    due:      { bg: '#fef9c3', color: '#854d0e' },
    overdue:  { bg: '#ffedd5', color: '#9a3412' },
    lapsed:   { bg: '#fee2e2', color: '#dc2626' },
  };

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

  return (
    <div>
      {/* Revenue summary */}
      {data?.revenue && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
          <StatCard label="Renewals This Month" value={data.revenue.renewals_this_month} />
          <StatCard label="Revenue This Month" value={`$${parseFloat(data.revenue.renewal_revenue_this_month).toFixed(2)}`} />
          <StatCard label="Revenue This FY" value={`$${parseFloat(data.revenue.renewal_revenue_this_fy).toFixed(2)}`} />
          <StatCard label="Revenue Last FY" value={`$${parseFloat(data.revenue.renewal_revenue_last_fy).toFixed(2)}`} />
        </div>
      )}

      {/* Status filter pills */}
      {data?.counts && (
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
          {[
            { key: '', label: 'All' },
            { key: 'due',     label: `Due (${data.counts.due})` },
            { key: 'overdue', label: `Overdue (${data.counts.overdue})` },
            { key: 'lapsed',  label: `Lapsed (${data.counts.lapsed})` },
            { key: 'active',  label: `Active (${data.counts.current})` },
          ].map(({ key, label }) => (
            <button key={key} onClick={() => setFilter(key)} style={{
              padding: '0.35rem 0.9rem', borderRadius: '9999px', border: '1.5px solid',
              fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer',
              background: filter === key ? '#1a1a2e' : '#fff',
              color: filter === key ? '#fff' : '#374151',
              borderColor: filter === key ? '#1a1a2e' : '#d1d5db',
            }}>{label}</button>
          ))}
        </div>
      )}

      {/* Search + bulk actions */}
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.25rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          placeholder="Search name, email, number…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ padding: '0.5rem 0.85rem', border: '1.5px solid #e2e2e2', borderRadius: '8px', fontSize: '0.875rem', minWidth: '240px' }}
        />
        {(filter || selected.size > 0) && !isViewer && (
          <button
            onClick={handleBulkEmail}
            disabled={bulkSending}
            style={{
              padding: '0.5rem 1rem', background: '#1a1a2e', color: '#fff',
              border: 'none', borderRadius: '7px', fontWeight: 600, fontSize: '0.85rem',
              cursor: bulkSending ? 'not-allowed' : 'pointer', opacity: bulkSending ? 0.7 : 1,
            }}
          >
            {bulkSending ? 'Sending…' : selected.size > 0 ? `Email ${selected.size} Selected` : `Email All ${filter ? filter.charAt(0).toUpperCase() + filter.slice(1) : ''}`}
          </button>
        )}
        {!isViewer && (
          <button
            onClick={handleExportCSV}
            style={{
              padding: '0.5rem 1rem', background: '#f1f5f9', color: '#374151',
              border: '1.5px solid #d1d5db', borderRadius: '7px', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer',
            }}
          >
            Export CSV
          </button>
        )}
        {bulkMsg && <span style={{ fontSize: '0.85rem', color: bulkMsg.includes('Failed') ? '#dc2626' : '#15803d' }}>{bulkMsg}</span>}
      </div>

      {loading ? (
        <p style={{ color: '#6b7280' }}>Loading…</p>
      ) : !data?.members?.length ? (
        <p style={{ color: '#9ca3af', fontStyle: 'italic' }}>No members match this filter.</p>
      ) : (
        <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: '12px', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: '2px solid var(--border)' }}>
                <th style={{ padding: '0.65rem 0.9rem', width: '36px' }}>
                  <input type="checkbox"
                    checked={selected.size === data.members.length && data.members.length > 0}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th style={{ textAlign: 'left', padding: '0.65rem 0.9rem', color: '#6b7280', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase' }}>Member</th>
                <th style={{ textAlign: 'left', padding: '0.65rem 0.9rem', color: '#6b7280', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase' }}>Type</th>
                <th style={{ textAlign: 'left', padding: '0.65rem 0.9rem', color: '#6b7280', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase' }}>Expiry</th>
                <th style={{ textAlign: 'left', padding: '0.65rem 0.9rem', color: '#6b7280', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase' }}>Status</th>
                <th style={{ textAlign: 'left', padding: '0.65rem 0.9rem', color: '#6b7280', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase' }}>Fee</th>
                <th style={{ padding: '0.65rem 0.9rem' }}></th>
              </tr>
            </thead>
            <tbody>
              {data.members.map(m => {
                const rs = m.status || 'active';
                const rStyle = RENEWAL_STATUS_STYLES[rs] || RENEWAL_STATUS_STYLES.active;
                const busy = actionBusy === m.member_id || actionBusy === m.member_id + '_remind';
                return (
                  <tr key={m.member_id} style={{ borderBottom: '1px solid #f1f5f9', background: selected.has(m.member_id) ? '#f8f9ff' : undefined }}>
                    <td style={{ padding: '0.75rem 0.9rem', textAlign: 'center' }}>
                      <input type="checkbox" checked={selected.has(m.member_id)} onChange={() => toggleSelect(m.member_id)} />
                    </td>
                    <td style={{ padding: '0.75rem 0.9rem' }}>
                      <div style={{ fontWeight: 600 }}>{m.full_name}</div>
                      <div style={{ fontSize: '0.78rem', color: '#6b7280' }}>{m.email}</div>
                      <div style={{ fontSize: '0.72rem', color: '#9ca3af', fontFamily: 'monospace' }}>{m.membership_number}</div>
                    </td>
                    <td style={{ padding: '0.75rem 0.9rem', color: '#374151' }}>{m.membership_type_name || '—'}</td>
                    <td style={{ padding: '0.75rem 0.9rem', color: '#374151' }}>
                      <span>{fmtDate(m.expiry_date)}</span>
                    </td>
                    <td style={{ padding: '0.75rem 0.9rem' }}>
                      <span style={{ background: rStyle.bg, color: rStyle.color, padding: '0.2rem 0.6rem', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: 600 }}>
                        {rs.charAt(0).toUpperCase() + rs.slice(1)}
                      </span>
                    </td>
                    <td style={{ padding: '0.75rem 0.9rem', color: '#374151' }}>
                      {m.fee ? `$${parseFloat(m.fee).toFixed(2)}` : '—'}
                    </td>
                    <td style={{ padding: '0.75rem 0.9rem' }}>
                      <div style={{ display: 'flex', gap: '0.4rem', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                        {!isViewer && (
                          <button
                            onClick={() => handleSendReminder(m)}
                            disabled={busy}
                            title="Send reminder email"
                            style={{
                              background: remindFlash[m.member_id] === 'Sent!' ? '#dcfce7' : remindFlash[m.member_id] === 'Failed' ? '#fee2e2' : '#f8fafc',
                              border: `1.5px solid ${remindFlash[m.member_id] === 'Sent!' ? '#86efac' : remindFlash[m.member_id] === 'Failed' ? '#fca5a5' : '#e2e8f0'}`,
                              color: remindFlash[m.member_id] === 'Sent!' ? '#15803d' : remindFlash[m.member_id] === 'Failed' ? '#dc2626' : '#374151',
                              borderRadius: '6px', padding: '0.25rem 0.6rem', fontSize: '0.75rem', fontWeight: 600,
                              cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.6 : 1,
                            }}
                          >
                            {actionBusy === m.member_id + '_remind' ? 'Sending…' : remindFlash[m.member_id] || 'Remind'}
                          </button>
                        )}
                        {rs !== 'active' && !isViewer && (
                          <button
                            onClick={() => handleManualRenew(m.member_id)}
                            disabled={busy}
                            style={{ background: 'none', border: '1.5px solid #1a1a2e', color: '#1a1a2e', borderRadius: '6px', padding: '0.25rem 0.6rem', fontSize: '0.75rem', fontWeight: 600, cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.6 : 1 }}
                          >
                            {actionBusy === m.member_id ? '…' : 'Mark Renewed'}
                          </button>
                        )}
                        {!isViewer && (
                          <button
                            onClick={() => { setExpiryModal({ memberId: m.member_id, current: m.expiry_date }); setExpiryInput(m.expiry_date ? m.expiry_date.split('T')[0] : ''); setExpiryMsg(''); }}
                            style={{ background: '#f8fafc', border: '1.5px solid #e2e8f0', color: '#374151', borderRadius: '6px', padding: '0.25rem 0.6rem', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer' }}
                          >
                            Set Expiry
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Override expiry modal */}
      {expiryModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => setExpiryModal(null)}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '1.5rem', maxWidth: '360px', width: '90%' }}
            onClick={e => e.stopPropagation()}>
            <h3 style={{ fontWeight: 700, marginBottom: '1rem', fontSize: '1rem' }}>Override Expiry Date</h3>
            <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '1rem' }}>
              Current: <strong>{fmtDate(expiryModal.current)}</strong>
            </p>
            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label style={{ fontSize: '0.8rem' }}>New Expiry Date</label>
              <input type="date" value={expiryInput} onChange={e => setExpiryInput(e.target.value)} />
            </div>
            {expiryMsg && <p style={{ color: '#dc2626', fontSize: '0.8rem', marginBottom: '0.75rem' }}>{expiryMsg}</p>}
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button onClick={handleExpiryOverride} style={{ flex: 1, background: '#1a1a2e', color: '#fff', border: 'none', borderRadius: '7px', padding: '0.5rem', fontWeight: 600, cursor: 'pointer' }}>
                Save
              </button>
              <button onClick={() => setExpiryModal(null)} style={{ background: '#f1f5f9', color: '#374151', border: 'none', borderRadius: '7px', padding: '0.5rem 1rem', cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Lapsed Tab ───────────────────────────────────────────────────────────────
const SOURCE_STYLES = {
  lapsed_member:     { bg: '#fee2e2', color: '#991b1b', label: 'Lapsed Member' },
  incomplete_signup: { bg: '#fef3c7', color: '#92400e', label: 'Incomplete Signup' },
  legacy_expired:    { bg: '#f3f4f6', color: '#6b7280', label: 'Legacy Expired' },
};

function LapsedTab() {
  const isViewer = getStoredUser().role === 'viewer';

  const [records,        setRecords]        = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [filter,         setFilter]         = useState('');
  const [selected,       setSelected]       = useState(new Set()); // Set of 'member-{id}' | 'prospect-{id}'
  const [deleteTarget,   setDeleteTarget]   = useState(null);      // { type, id, name }
  const [deleting,       setDeleting]       = useState(false);
  const [winbackConfirm, setWinbackConfirm] = useState(null);      // { count, ids }
  const [sending,        setSending]        = useState(false);
  const [sendMsg,        setSendMsg]        = useState('');
  const [winbackTmpl,    setWinbackTmpl]    = useState(null);
  const [tmplEdits,      setTmplEdits]      = useState({ subject: '', body: '' });
  const [tmplSaving,     setTmplSaving]     = useState(false);
  const [tmplMsg,        setTmplMsg]        = useState('');
  const [testPhase,      setTestPhase]      = useState(null);       // null | 'confirming' | 'sending'
  const [testMsg,        setTestMsg]        = useState('');

  const loadRecords = useCallback(() => {
    setLoading(true);
    apiClient().get('/api/admin/lapsed')
      .then(res => { setRecords(res.data); setSelected(new Set()); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadRecords();
    apiClient().get('/api/admin/email-templates')
      .then(res => {
        const t = res.data.find(t => t.template_key === 'winback');
        if (t) { setWinbackTmpl(t); setTmplEdits({ subject: t.subject, body: t.body }); }
      })
      .catch(console.error);
  }, [loadRecords]);

  // Clear selection + any pending confirm when filter changes
  useEffect(() => {
    setSelected(new Set());
    setSendMsg('');
    setWinbackConfirm(null);
  }, [filter]);

  // ── Derived ──────────────────────────────────────────────────────────────
  const filteredRecords = filter ? records.filter(r => r.source === filter) : records;
  const counts = {
    lapsed_member:     records.filter(r => r.source === 'lapsed_member').length,
    incomplete_signup: records.filter(r => r.source === 'incomplete_signup').length,
    legacy_expired:    records.filter(r => r.source === 'legacy_expired').length,
  };

  const rowKey          = (r)  => `${r.type}-${r.id}`;
  const allFilteredSel  = filteredRecords.length > 0 && filteredRecords.every(r => selected.has(rowKey(r)));
  const fmtDate         = (iso) => iso
    ? new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
    : '—';

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleSelectAll = () => {
    if (allFilteredSel) {
      setSelected(prev => {
        const next = new Set(prev);
        filteredRecords.forEach(r => next.delete(rowKey(r)));
        return next;
      });
    } else {
      setSelected(prev => {
        const next = new Set(prev);
        filteredRecords.forEach(r => next.add(rowKey(r)));
        return next;
      });
    }
  };

  const toggleSelect = (r) => {
    const k = rowKey(r);
    setSelected(prev => { const n = new Set(prev); n.has(k) ? n.delete(k) : n.add(k); return n; });
  };

  const buildIds = (keys) => keys.map(k => {
    const dash = k.indexOf('-');
    return { type: k.slice(0, dash), id: parseInt(k.slice(dash + 1), 10) };
  });

  const handleConfirmSend = (ids) => {
    setSendMsg('');
    setWinbackConfirm({ count: ids.length, ids });
  };

  const handleSendWinback = async () => {
    if (!winbackConfirm) return;
    setSending(true); setSendMsg('');
    try {
      const res = await apiClient().post('/api/admin/lapsed/send-winback', { recipient_ids: winbackConfirm.ids });
      const { sent, failed } = res.data;
      setSendMsg(`Sent ${sent}${failed > 0 ? `, ${failed} failed` : ''}.`);
      setSelected(new Set());
    } catch (err) {
      setSendMsg(err.response?.data?.error || 'Failed to send.');
    } finally {
      setSending(false);
      setWinbackConfirm(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await apiClient().delete(`/api/admin/lapsed/${deleteTarget.type}/${deleteTarget.id}`);
      setDeleteTarget(null);
      loadRecords();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to remove.');
      setDeleting(false);
    }
  };

  const handleSaveTmpl = async () => {
    if (!winbackTmpl) return;
    setTmplSaving(true); setTmplMsg('');
    try {
      await apiClient().put(`/api/admin/email-templates/${winbackTmpl.template_id}`, tmplEdits);
      setTmplMsg('Saved.');
    } catch (err) {
      setTmplMsg(err.response?.data?.error || 'Failed to save.');
    } finally { setTmplSaving(false); }
  };

  const handleSendTest = async () => {
    if (!winbackTmpl) return;
    setTestPhase('sending'); setTestMsg('');
    try {
      const res = await apiClient().post(`/api/admin/email-templates/${winbackTmpl.template_id}/test`);
      setTestMsg(res.data.message || 'Test email sent.');
    } catch (err) {
      setTestMsg(err.response?.data?.message || 'Failed to send test.');
    } finally { setTestPhase(null); }
  };

  // ── Shared styles ─────────────────────────────────────────────────────────
  const thStyle = { padding: '0.65rem 0.9rem', textAlign: 'left', color: '#6b7280', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase' };

  if (loading) return <p style={{ color: '#6b7280' }}>Loading…</p>;

  return (
    <div>

      {/* ══ SECTION 1 — Prospects list ══════════════════════════════════════ */}

      {/* Summary */}
      <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1rem' }}>
        <strong style={{ color: '#374151' }}>{counts.lapsed_member}</strong> lapsed member{counts.lapsed_member !== 1 ? 's' : ''}{' · '}
        <strong style={{ color: '#374151' }}>{counts.incomplete_signup}</strong> incomplete signup{counts.incomplete_signup !== 1 ? 's' : ''}{' · '}
        <strong style={{ color: '#374151' }}>{counts.legacy_expired}</strong> legacy expired
      </p>

      {/* Filter pills */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
        {[
          { key: '',                  label: `All (${records.length})` },
          { key: 'lapsed_member',     label: `Lapsed Members (${counts.lapsed_member})` },
          { key: 'incomplete_signup', label: `Incomplete Signups (${counts.incomplete_signup})` },
          { key: 'legacy_expired',    label: `Legacy Expired (${counts.legacy_expired})` },
        ].map(({ key, label }) => (
          <button key={key} onClick={() => setFilter(key)} style={{
            padding: '0.35rem 0.9rem', borderRadius: '9999px', border: '1.5px solid',
            fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer',
            background: filter === key ? '#1a1a2e' : '#fff',
            color:      filter === key ? '#fff'    : '#374151',
            borderColor: filter === key ? '#1a1a2e' : '#d1d5db',
          }}>{label}</button>
        ))}
      </div>

      {/* Bulk action bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap', minHeight: '36px' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', color: '#374151', userSelect: 'none' }}>
          <input
            type="checkbox"
            checked={allFilteredSel}
            onChange={handleSelectAll}
            disabled={filteredRecords.length === 0}
          />
          Select All
        </label>

        {winbackConfirm ? (
          <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', fontSize: '0.85rem', color: '#374151' }}>
            <span>This will send a win-back email to <strong>{winbackConfirm.count}</strong> {winbackConfirm.count === 1 ? 'person' : 'people'}. Continue?</span>
            <button
              onClick={handleSendWinback}
              disabled={sending}
              style={{ padding: '0.35rem 0.9rem', background: '#1a1a2e', color: '#fff', border: 'none', borderRadius: '6px', fontWeight: 600, fontSize: '0.8rem', cursor: sending ? 'not-allowed' : 'pointer', opacity: sending ? 0.7 : 1 }}
            >
              {sending ? 'Sending…' : 'Yes, send it'}
            </button>
            <button
              onClick={() => setWinbackConfirm(null)}
              disabled={sending}
              style={{ padding: '0.35rem 0.9rem', background: '#f1f5f9', color: '#374151', border: '1.5px solid #d1d5db', borderRadius: '6px', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer' }}
            >
              Cancel
            </button>
          </span>
        ) : (
          <>
            {!isViewer && (
              <button
                onClick={() => handleConfirmSend(buildIds([...selected]))}
                disabled={selected.size === 0}
                style={{
                  padding: '0.4rem 1rem', background: '#1a1a2e', color: '#fff',
                  border: 'none', borderRadius: '7px', fontWeight: 600, fontSize: '0.85rem',
                  cursor: selected.size === 0 ? 'not-allowed' : 'pointer',
                  opacity: selected.size === 0 ? 0.45 : 1,
                }}
              >
                Send Win-Back Email {selected.size > 0 ? `(${selected.size})` : ''}
              </button>
            )}
            {!isViewer && (
              <button
                onClick={() => handleConfirmSend(filteredRecords.map(r => ({ type: r.type, id: r.id })))}
                disabled={filteredRecords.length === 0}
                style={{
                  padding: '0.4rem 1rem', background: '#f1f5f9', color: '#374151',
                  border: '1.5px solid #d1d5db', borderRadius: '7px', fontWeight: 600, fontSize: '0.85rem',
                  cursor: filteredRecords.length === 0 ? 'not-allowed' : 'pointer',
                  opacity: filteredRecords.length === 0 ? 0.45 : 1,
                }}
              >
                Send to All
              </button>
            )}
          </>
        )}
        {sendMsg && (
          <span style={{ fontSize: '0.85rem', color: sendMsg.includes('failed') || sendMsg.includes('Failed') ? '#dc2626' : '#15803d' }}>
            {sendMsg}
          </span>
        )}
      </div>

      {/* Table */}
      {filteredRecords.length === 0 ? (
        <p style={{ color: '#9ca3af', fontStyle: 'italic', fontSize: '0.875rem' }}>
          No records match this filter.
        </p>
      ) : (
        <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: '12px', overflow: 'hidden', marginBottom: '2.5rem' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: '2px solid var(--border)' }}>
                <th style={{ padding: '0.65rem 0.9rem', width: '36px' }}></th>
                <th style={thStyle}>Name</th>
                <th style={thStyle}>Email</th>
                <th style={thStyle}>Type</th>
                <th style={thStyle}>Source</th>
                <th style={thStyle}>Date</th>
                {!isViewer && <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {filteredRecords.map((r, i) => {
                const k   = rowKey(r);
                const src = SOURCE_STYLES[r.source] || SOURCE_STYLES.legacy_expired;
                return (
                  <tr key={k} style={{ borderBottom: i < filteredRecords.length - 1 ? '1px solid #f1f5f9' : 'none', background: selected.has(k) ? '#f8f9ff' : undefined }}>
                    <td style={{ padding: '0.75rem 0.9rem', textAlign: 'center' }}>
                      <input type="checkbox" checked={selected.has(k)} onChange={() => toggleSelect(r)} />
                    </td>
                    <td style={{ padding: '0.75rem 0.9rem' }}>
                      <div style={{ fontWeight: 600, color: '#111827' }}>
                        {r.full_name || [r.first_name, r.last_name].filter(Boolean).join(' ') || '—'}
                      </div>
                    </td>
                    <td style={{ padding: '0.75rem 0.9rem', color: '#374151' }}>{r.email}</td>
                    <td style={{ padding: '0.75rem 0.9rem', color: '#6b7280' }}>{r.membership_type || '—'}</td>
                    <td style={{ padding: '0.75rem 0.9rem' }}>
                      <span style={{ background: src.bg, color: src.color, borderRadius: '9999px', padding: '0.2rem 0.6rem', fontSize: '0.75rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
                        {src.label}
                      </span>
                    </td>
                    <td style={{ padding: '0.75rem 0.9rem', color: '#6b7280', fontSize: '0.82rem' }}>{fmtDate(r.date)}</td>
                    {!isViewer && (
                      <td style={{ padding: '0.75rem 0.9rem', textAlign: 'right' }}>
                        <button
                          type="button"
                          onClick={() => setDeleteTarget({ type: r.type, id: r.id, name: r.full_name || r.email })}
                          style={{ padding: '0.25rem 0.7rem', background: '#fee2e2', color: '#dc2626', border: '1px solid #fca5a5', borderRadius: '6px', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer' }}
                        >
                          Delete
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ══ SECTION 2 — Win-Back Email Template ═════════════════════════════ */}
      <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: '12px', padding: '1.5rem', maxWidth: '700px' }}>
        <h3 style={{ fontWeight: 700, fontSize: '1rem', marginBottom: '1.25rem' }}>Win-Back Email Template</h3>

        {!winbackTmpl ? (
          <p style={{ color: '#9ca3af', fontSize: '0.875rem' }}>
            Win-back template not found. Run migration 012_winback_email_template.sql first.
          </p>
        ) : (
          <>
            <div className="form-group">
              <label>Subject</label>
              <input
                value={tmplEdits.subject}
                onChange={e => setTmplEdits(p => ({ ...p, subject: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <label>Body</label>
              <textarea
                rows={8}
                value={tmplEdits.body}
                onChange={e => setTmplEdits(p => ({ ...p, body: e.target.value }))}
                style={{ width: '100%', padding: '0.75rem 1rem', border: '1.5px solid var(--border)', borderRadius: '8px', fontSize: '0.875rem', fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
              {!isViewer && (
                <button
                  type="button"
                  onClick={handleSaveTmpl}
                  disabled={tmplSaving}
                  className="btn btn-primary"
                  style={{ width: 'auto', padding: '0.5rem 1.25rem' }}
                >
                  {tmplSaving ? 'Saving…' : 'Save Template'}
                </button>
              )}
              {!isViewer && (testPhase === 'confirming' ? (
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', fontSize: '0.85rem', color: '#374151' }}>
                  <span>Sends a test to <strong>{getStoredUser().email}</strong>. Continue?</span>
                  <button
                    type="button"
                    onClick={handleSendTest}
                    style={{ padding: '0.35rem 0.9rem', background: '#1a1a2e', color: '#fff', border: 'none', borderRadius: '6px', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer' }}
                  >
                    Yes, send it
                  </button>
                  <button
                    type="button"
                    onClick={() => setTestPhase(null)}
                    style={{ padding: '0.35rem 0.9rem', background: '#f1f5f9', color: '#374151', border: '1.5px solid #d1d5db', borderRadius: '6px', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer' }}
                  >
                    Cancel
                  </button>
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => { setTestPhase('confirming'); setTestMsg(''); }}
                  disabled={testPhase === 'sending'}
                  style={{
                    width: 'auto', padding: '0.5rem 1.25rem',
                    background: '#f1f5f9', color: '#374151',
                    border: '1.5px solid #d1d5db', borderRadius: '8px',
                    fontWeight: 600, fontSize: '0.875rem',
                    cursor: testPhase === 'sending' ? 'not-allowed' : 'pointer',
                    opacity: testPhase === 'sending' ? 0.7 : 1,
                  }}
                >
                  {testPhase === 'sending' ? 'Sending…' : 'Send Test'}
                </button>
              ))}
              {tmplMsg && <span style={{ fontSize: '0.8rem', color: tmplMsg === 'Saved.' ? '#15803d' : '#dc2626' }}>{tmplMsg}</span>}
              {testMsg  && <span style={{ fontSize: '0.8rem', color: testMsg.toLowerCase().includes('fail') ? '#dc2626' : '#15803d' }}>{testMsg}</span>}
            </div>
            <p style={{ fontSize: '0.78rem', color: '#9ca3af', marginTop: '1rem', marginBottom: 0 }}>
              Available placeholders: <code>{'{{first_name}}'}</code>, <code>{'{{org_name}}'}</code>, <code>{'{{custom_message}}'}</code>, <code>{'{{join_url}}'}</code>, <code>{'{{admin_email}}'}</code>
            </p>
          </>
        )}
      </div>

      {/* Delete confirmation modal */}
      {deleteTarget && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '2rem', width: '100%', maxWidth: '400px', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
            <h3 style={{ fontWeight: 700, marginBottom: '0.75rem', fontSize: '1.05rem' }}>Remove from Lapsed List</h3>
            <p style={{ fontSize: '0.9rem', color: '#374151', marginBottom: '1.5rem' }}>
              Remove <strong>{deleteTarget.name}</strong>?
              {deleteTarget.type === 'member'
                ? ' Their membership will be marked as cancelled.'
                : ' They will be permanently deleted from the prospects list.'}
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
                style={{ padding: '0.5rem 1.25rem', background: '#f1f5f9', color: '#374151', border: '1.5px solid #d1d5db', borderRadius: '8px', fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleting}
                style={{ padding: '0.5rem 1.25rem', background: '#dc2626', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 600, fontSize: '0.875rem', cursor: deleting ? 'not-allowed' : 'pointer', opacity: deleting ? 0.7 : 1 }}
              >
                {deleting ? 'Removing…' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

// Templates surfaced in the Emails tab for editing/refinement. Includes the
// win-back template so it can be edited here (as well as inline in the Lapsed tab).
//
// THIS IS AN ALLOW-LIST, AND THAT IS THE RISK: a template the system genuinely
// sends is invisible here until its key is added below. password_reset was in
// exactly that state — sent on every password reset (routes/auth.js), and
// impossible to read, test or correct from the admin UI. The same shape of
// oversight left a broken booking-manager invite live in the Performer app for a
// week, because the one email nobody could see was the one that was wrong.
//
// WHEN YOU ADD A TEMPLATE TO THE DATABASE, ADD ITS KEY HERE TOO.
//
// A 'welcome' row also existed here, with no code references anywhere and a
// subject duplicating welcome_new_member. It was deleted from email_templates on
// 20 Aug 2026 (backed up to memember-welcome-template-backup-20260820-153002.sql
// in the DEVELOPMENT folder). Every row in the table is now visible in this tab,
// which is the state to keep it in.
// Grouped by where the member is in their lifecycle, which is how anyone looking
// for a template actually thinks about it.
//
// FOOM and Performer group by AUDIENCE ("Emails to judges", "Emails to booking
// managers") because those apps write to several different kinds of people.
// Every email here goes to a member, so an audience axis would put all ten in
// one group and tell the reader nothing. The pattern is the same - grouped,
// collapsible, with a count - while the axis suits the app.
const TEMPLATE_GROUPS = [
  { id: 'joining',  title: 'Joining and welcome',  keys: ['welcome_new_member', 'welcome_set_password', 'finish_signup'] },
  { id: 'renewals', title: 'Renewals',             keys: ['renewal_due', 'renewal_grace', 'renewal_expiry', 'renewal_lapsed', 'renewal_confirmed'] },
  { id: 'lapsed',   title: 'Lapsed and win-back',  keys: ['winback'] },
  { id: 'account',  title: 'Account and security', keys: ['password_reset'] },
];

// Derived, never hand-maintained: a template listed in a group above is visible,
// one that is not listed is not. Keeping a second parallel array is exactly how
// password_reset came to be live but invisible in the first place.
const VISIBLE_TEMPLATE_KEYS = TEMPLATE_GROUPS.flatMap((g) => g.keys);
const TEMPLATE_LABELS = {
  welcome_new_member: 'Welcome — New Member',
  renewal_confirmed:  'Renewal Confirmation',
  renewal_due:        'Renewal Due',
  renewal_expiry:     'Membership Expired',
  renewal_grace:      'Grace Period Warning',
  renewal_lapsed:     'Membership Lapsed',
  winback:            'Win-Back (lapsed re-engagement)',
  welcome_set_password: 'Welcome — Set Your Password (migration)',
  finish_signup:        'Finish Sign-Up (abandoned registration)',
  password_reset:       'Password Reset',
};

/** Collapsible header for a group of templates. Mirrors the same control in the
 *  FOOM and Performer admin panels so the three apps read alike. */
function EmailGroup({ title, count, open, onToggle, children }) {
  return (
    <div style={{ marginBottom: open ? '1.5rem' : '0.6rem' }}>
      <div
        onClick={onToggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }}
        style={{
          display: 'flex', alignItems: 'center', gap: '0.6rem', cursor: 'pointer', userSelect: 'none',
          background: '#1a1a2e', color: '#fff', padding: '0.6rem 1rem', borderRadius: '8px',
          marginBottom: open ? '1rem' : 0,
        }}
      >
        <span style={{ display: 'inline-block', transition: 'transform 0.15s', transform: open ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
        <span style={{ fontSize: '0.95rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{title}</span>
        <span style={{ fontSize: '0.78rem', opacity: 0.7, marginLeft: 'auto' }}>
          {count} email{count === 1 ? '' : 's'}
        </span>
      </div>
      {open && children}
    </div>
  );
}

/** The addresses every Test button on this tab sends to. Sits above the
 *  templates because a Test button is only as useful as this list. */
function TestRecipientsPanel({ recipients, onAdd, onRemove, adding, removing, error, adminEmail, readOnly }) {
  const [email, setEmail] = React.useState('');
  const submit = (e) => {
    e.preventDefault();
    const t = email.trim();
    if (t) onAdd(t, () => setEmail(''));
  };
  return (
    <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '10px', padding: '1rem 1.25rem', marginBottom: '1.5rem' }}>
      <div style={{ fontSize: '0.72rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#92400e', marginBottom: '0.4rem' }}>
        Test Email Recipients
      </div>
      <p style={{ fontSize: '0.83rem', color: '#78350f', margin: '0 0 0.85rem', lineHeight: 1.5 }}>
        These addresses receive every <strong>Test</strong> send on this tab. A test uses
        sample details, so placeholders are filled with example values rather than a real
        member's. {recipients.length === 0 && (
          <>With the list empty, tests go to your own address (<strong>{adminEmail}</strong>).</>
        )}
      </p>
      {error && <div style={{ color: '#b91c1c', fontSize: '0.83rem', marginBottom: '0.6rem' }}>{error}</div>}
      {!readOnly && (
        <form onSubmit={submit} style={{ display: 'flex', gap: '0.5rem', marginBottom: recipients.length ? '0.85rem' : 0 }}>
          <input
            type="email"
            placeholder="name@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={{ maxWidth: '320px', flex: '1 1 auto', padding: '0.45rem 0.7rem', border: '1.5px solid #fde68a', borderRadius: '7px', fontSize: '0.875rem' }}
          />
          <button
            type="submit"
            disabled={adding || !email.trim()}
            style={{ padding: '0.45rem 1rem', background: '#1a1a2e', color: '#fff', border: 'none', borderRadius: '7px', fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer' }}
          >
            {adding ? 'Adding…' : '+ Add'}
          </button>
        </form>
      )}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
        {recipients.map((r) => (
          <span key={r.id} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', background: '#fff', border: '1px solid #fde68a', borderRadius: '999px', padding: '0.28rem 0.75rem', fontSize: '0.83rem', color: '#374151' }}>
            {r.email}
            {!readOnly && (
              <button
                type="button"
                onClick={() => onRemove(r)}
                disabled={removing === r.id}
                title={`Remove ${r.email}`}
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#dc2626', fontSize: '1rem', lineHeight: 1 }}
              >
                {removing === r.id ? '…' : '×'}
              </button>
            )}
          </span>
        ))}
      </div>
    </div>
  );
}

function EmailsTab() {
  const isViewer = getStoredUser().role === 'viewer';
  const adminEmail = getStoredUser().email || '';

  const [templates, setTemplates] = useState([]);
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState({});
  const [msgs, setMsgs] = useState({});
  // testPhase[id]: 'idle' | 'confirming' | 'sending' | { ok: bool, msg: string }
  const [testPhase, setTestPhase] = useState({});
  const [loading, setLoading] = useState(true);
  const [openGroups, setOpenGroups] = useState(
    () => Object.fromEntries(TEMPLATE_GROUPS.map((g) => [g.id, true]))
  );

  // ── Test recipients ──
  const [recipients, setRecipients]     = useState([]);
  const [rcptError, setRcptError]       = useState('');
  const [addingRcpt, setAddingRcpt]     = useState(false);
  const [removingRcpt, setRemovingRcpt] = useState(null);

  const toggleGroup = (id) => setOpenGroups((o) => ({ ...o, [id]: !o[id] }));

  const handleAddRecipient = async (email, clear) => {
    setAddingRcpt(true);
    setRcptError('');
    try {
      const res = await apiClient().post('/api/admin/test-email-recipients', { email });
      setRecipients((l) => [...l, res.data].sort((a, b) => a.email.toLowerCase().localeCompare(b.email.toLowerCase())));
      clear();
    } catch (err) {
      setRcptError(err.response?.data?.error || 'Failed to add that address.');
    } finally {
      setAddingRcpt(false);
    }
  };

  const handleRemoveRecipient = async (row) => {
    setRemovingRcpt(row.id);
    setRcptError('');
    try {
      await apiClient().delete(`/api/admin/test-email-recipients/${row.id}`);
      setRecipients((l) => l.filter((r) => r.id !== row.id));
    } catch (err) {
      setRcptError(err.response?.data?.error || 'Failed to remove that address.');
    } finally {
      setRemovingRcpt(null);
    }
  };

  useEffect(() => {
    apiClient().get('/api/admin/test-email-recipients')
      .then(res => setRecipients(Array.isArray(res.data) ? res.data : []))
      .catch(() => setRcptError('Failed to load the test recipients list.'));
  }, []);

  useEffect(() => {
    apiClient().get('/api/admin/email-templates')
      .then(res => {
        const filtered = (Array.isArray(res.data) ? res.data : []).filter(t => VISIBLE_TEMPLATE_KEYS.includes(t.template_key));
        setTemplates(filtered);
        const initial = {};
        filtered.forEach(t => { initial[t.template_id] = { subject: t.subject, body: t.body }; });
        setEdits(initial);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async (templateId) => {
    setSaving(s => ({ ...s, [templateId]: true }));
    setMsgs(m => ({ ...m, [templateId]: '' }));
    try {
      await apiClient().put(`/api/admin/email-templates/${templateId}`, edits[templateId]);
      setMsgs(m => ({ ...m, [templateId]: 'Saved.' }));
    } catch (err) {
      setMsgs(m => ({ ...m, [templateId]: err.response?.data?.error || 'Failed to save.' }));
    } finally {
      setSaving(s => ({ ...s, [templateId]: false }));
    }
  };

  const handleConfirmTest = (templateId) => {
    setTestPhase(p => ({ ...p, [templateId]: 'confirming' }));
  };

  const handleCancelTest = (templateId) => {
    setTestPhase(p => ({ ...p, [templateId]: 'idle' }));
  };

  const handleSendTest = async (templateId) => {
    setTestPhase(p => ({ ...p, [templateId]: 'sending' }));
    try {
      const res = await apiClient().post(`/api/admin/email-templates/${templateId}/test`);
      setTestPhase(p => ({ ...p, [templateId]: { ok: true, msg: res.data.message || 'Sent!' } }));
    } catch (err) {
      setTestPhase(p => ({ ...p, [templateId]: { ok: false, msg: err.response?.data?.message || 'Failed to send.' } }));
    }
  };

  // Renders the templates arranged into TEMPLATE_GROUPS, given a function that
  // renders one template. Takes the per-template renderer as an argument rather
  // than inlining the grouping, so the (long) card markup below stays exactly
  // where it was and the grouping is readable on its own.
  const byKey = Object.fromEntries(templates.map((t) => [t.template_key, t]));
  const renderGrouped = (renderOne) =>
    TEMPLATE_GROUPS.map((g) => {
      const present = g.keys.map((k) => byKey[k]).filter(Boolean);
      if (present.length === 0) return null;
      return (
        <EmailGroup
          key={g.id}
          title={g.title}
          count={present.length}
          open={openGroups[g.id]}
          onToggle={() => toggleGroup(g.id)}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {present.map(renderOne)}
          </div>
        </EmailGroup>
      );
    });

  if (loading) return <p style={{ color: '#6b7280' }}>Loading templates...</p>;
  if (templates.length === 0) return <p style={{ color: '#6b7280' }}>No email templates found.</p>;

  return (
    <div>
      <h2 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '1rem' }}>Email Templates</h2>

      <TestRecipientsPanel
        recipients={recipients}
        onAdd={handleAddRecipient}
        onRemove={handleRemoveRecipient}
        adding={addingRcpt}
        removing={removingRcpt}
        error={rcptError}
        adminEmail={adminEmail}
        readOnly={isViewer}
      />

      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {renderGrouped(t => {
          const edit    = edits[t.template_id] || { subject: t.subject, body: t.body };
          const isSaving = saving[t.template_id];
          const msg      = msgs[t.template_id];
          const phase    = testPhase[t.template_id] || 'idle';
          return (
            <div key={t.template_id} style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: '12px', padding: '1.25rem' }}>
              <h4 style={{ fontWeight: 600, fontSize: '0.875rem', marginBottom: '0.125rem', color: 'var(--primary)' }}>
                {TEMPLATE_LABELS[t.template_key] || t.template_key}
              </h4>
              <p style={{ fontSize: '0.72rem', color: '#9ca3af', fontFamily: 'monospace', marginBottom: '1rem' }}>{t.template_key}</p>
              <div className="form-group">
                <label>Subject</label>
                <input
                  value={edit.subject}
                  onChange={e => setEdits(prev => ({ ...prev, [t.template_id]: { ...prev[t.template_id], subject: e.target.value } }))}
                />
              </div>
              <div className="form-group">
                <label>Body</label>
                <textarea
                  rows={8}
                  value={edit.body}
                  onChange={e => setEdits(prev => ({ ...prev, [t.template_id]: { ...prev[t.template_id], body: e.target.value } }))}
                  style={{ width: '100%', padding: '0.75rem 1rem', border: '1.5px solid var(--border)', borderRadius: '8px', fontSize: '0.875rem', fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
                {!isViewer && (
                  <button
                    type="button"
                    onClick={() => handleSave(t.template_id)}
                    disabled={isSaving}
                    className="btn btn-primary"
                    style={{ width: 'auto', padding: '0.5rem 1.25rem' }}
                  >
                    {isSaving ? 'Saving...' : 'Save Template'}
                  </button>
                )}
                {!isViewer && (phase === 'confirming' ? (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', fontSize: '0.85rem', color: '#374151' }}>
                    <span>
                      This will send a test email to{' '}
                      <strong>
                        {recipients.length
                          ? recipients.map((r) => r.email).join(', ')
                          : adminEmail}
                      </strong>
                      {recipients.length ? '' : ' (your own address — no test recipients are set)'}. Continue?
                    </span>
                    <button
                      type="button"
                      onClick={() => handleSendTest(t.template_id)}
                      style={{ padding: '0.35rem 0.9rem', background: '#1a1a2e', color: '#fff', border: 'none', borderRadius: '6px', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer' }}
                    >
                      Yes, send it
                    </button>
                    <button
                      type="button"
                      onClick={() => handleCancelTest(t.template_id)}
                      style={{ padding: '0.35rem 0.9rem', background: '#f1f5f9', color: '#374151', border: '1.5px solid #d1d5db', borderRadius: '6px', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer' }}
                    >
                      Cancel
                    </button>
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => handleConfirmTest(t.template_id)}
                    disabled={phase === 'sending'}
                    style={{
                      width: 'auto', padding: '0.5rem 1.25rem',
                      background: '#f1f5f9', color: '#374151',
                      border: '1.5px solid #d1d5db', borderRadius: '8px',
                      fontWeight: 600, fontSize: '0.875rem',
                      cursor: phase === 'sending' ? 'not-allowed' : 'pointer',
                      opacity: phase === 'sending' ? 0.7 : 1,
                    }}
                  >
                    {phase === 'sending' ? 'Sending...' : 'Send Test'}
                  </button>
                ))}
                {msg && <span style={{ fontSize: '0.8rem', color: msg === 'Saved.' ? '#15803d' : '#dc2626' }}>{msg}</span>}
                {phase && phase.msg && (
                  <span style={{ fontSize: '0.8rem', color: phase.ok ? '#15803d' : '#dc2626' }}>{phase.msg}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Settings Tab ─────────────────────────────────────────────────────────────
function SettingsTab() {
  const isViewer = getStoredUser().role === 'viewer';
  const [form, setForm] = useState({
    name: '', logo_url: '', primary_colour: '#1a1a2e', secondary_colour: '#d4af37',
    sender_email: '', sender_name: '',
    grace_period_days: 30, renewal_reminder_days: 14, grace_reminder_days: 7,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    apiClient().get('/api/admin/settings')
      .then(res => setForm({
        name: res.data.name || '',
        logo_url: res.data.logo_url || '',
        primary_colour: res.data.primary_colour || '#1a1a2e',
        secondary_colour: res.data.secondary_colour || '#d4af37',
        sender_email: res.data.sender_email || '',
        sender_name: res.data.sender_name || '',
        grace_period_days: res.data.grace_period_days ?? 30,
        renewal_reminder_days: res.data.renewal_reminder_days ?? 14,
        grace_reminder_days: res.data.grace_reminder_days ?? 7,
      }))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true); setMsg('');
    try {
      await apiClient().put('/api/admin/settings', form);
      setMsg('Settings saved.');
    } catch (err) {
      setMsg(err.response?.data?.error || 'Failed to save settings.');
    } finally { setSaving(false); }
  };

  const numField = (key) => e => setForm(f => ({ ...f, [key]: parseInt(e.target.value) || 0 }));

  if (loading) return <p style={{ color: '#6b7280' }}>Loading settings...</p>;

  return (
    <div style={{ maxWidth: '600px' }}>
      <form onSubmit={handleSave}>
        <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: '12px', padding: '1.5rem', marginBottom: '1.5rem' }}>
          <h3 style={{ fontWeight: 600, marginBottom: '1.25rem', fontSize: '1rem' }}>Organisation Branding</h3>
          <div className="form-group"><label>Organisation Name</label><input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} /></div>
          <div className="form-group"><label>Logo URL</label><input value={form.logo_url} onChange={e => setForm(f => ({ ...f, logo_url: e.target.value }))} placeholder="https://..." /></div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <div className="form-group">
              <label>Primary Colour</label>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <input type="color" value={form.primary_colour} onChange={e => setForm(f => ({ ...f, primary_colour: e.target.value }))} style={{ width: '42px', height: '42px', border: '1.5px solid var(--border)', borderRadius: '8px', padding: '2px', cursor: 'pointer' }} />
                <input value={form.primary_colour} onChange={e => setForm(f => ({ ...f, primary_colour: e.target.value }))} style={{ flex: 1 }} />
              </div>
            </div>
            <div className="form-group">
              <label>Secondary Colour</label>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <input type="color" value={form.secondary_colour} onChange={e => setForm(f => ({ ...f, secondary_colour: e.target.value }))} style={{ width: '42px', height: '42px', border: '1.5px solid var(--border)', borderRadius: '8px', padding: '2px', cursor: 'pointer' }} />
                <input value={form.secondary_colour} onChange={e => setForm(f => ({ ...f, secondary_colour: e.target.value }))} style={{ flex: 1 }} />
              </div>
            </div>
          </div>
          <div className="form-group"><label>Sender Email</label><input type="email" value={form.sender_email} onChange={e => setForm(f => ({ ...f, sender_email: e.target.value }))} placeholder="noreply@yourorg.com" /></div>
          <div className="form-group"><label>Sender Name</label><input value={form.sender_name} onChange={e => setForm(f => ({ ...f, sender_name: e.target.value }))} placeholder="Your Organisation" /></div>
        </div>

        <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: '12px', padding: '1.5rem', marginBottom: '1.5rem' }}>
          <h3 style={{ fontWeight: 600, marginBottom: '1.25rem', fontSize: '1rem' }}>Renewal Settings</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <div className="form-group">
              <label>Grace Period (days)</label>
              <input type="number" min="0" max="365" value={form.grace_period_days} onChange={numField('grace_period_days')} style={{ maxWidth: '120px' }} />
              <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>Days after expiry before status becomes Lapsed</p>
            </div>
            <div className="form-group">
              <label>Renewal Reminder (days before expiry)</label>
              <input type="number" min="1" max="365" value={form.renewal_reminder_days} onChange={numField('renewal_reminder_days')} style={{ maxWidth: '120px' }} />
              <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>When to send the first reminder email</p>
            </div>
            <div className="form-group">
              <label>Grace Reminder (days into grace period)</label>
              <input type="number" min="1" max="365" value={form.grace_reminder_days} onChange={numField('grace_reminder_days')} style={{ maxWidth: '120px' }} />
              <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>Must be less than Grace Period</p>
            </div>
          </div>
        </div>

        {msg && <p style={{ color: msg.includes('Failed') || msg.includes('must') ? 'var(--error)' : '#15803d', fontSize: '0.875rem', marginBottom: '0.75rem' }}>{msg}</p>}
        {!isViewer && (
          <button type="submit" className="btn btn-primary" disabled={saving} style={{ width: 'auto', padding: '0.625rem 1.5rem' }}>
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        )}
      </form>
    </div>
  );
}

// ── Sender Email Card ─────────────────────────────────────────────────────────
// Displays and edits tenants.sender_email, sender_name, and reply_to.
// Loaded from GET /api/admin/settings; saved via PUT /api/admin/sender-email.
// Edit button and form hidden for viewers.
function SenderEmailCard() {
  const isViewer = getStoredUser().role === 'viewer';
  const [data, setData]       = useState({ sender_email: '', sender_name: '', reply_to: '' });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm]       = useState({ sender_email: '', sender_name: '', reply_to: '' });
  const [saving, setSaving]   = useState(false);
  const [msg, setMsg]         = useState('');

  useEffect(() => {
    apiClient().get('/api/admin/settings')
      .then(res => setData({
        sender_email: res.data.sender_email || '',
        sender_name:  res.data.sender_name  || '',
        reply_to:     res.data.reply_to     || '',
      }))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const openEdit = () => {
    setForm({ sender_email: data.sender_email, sender_name: data.sender_name, reply_to: data.reply_to });
    setMsg('');
    setEditing(true);
  };

  const handleCancel = () => { setEditing(false); setMsg(''); };

  const validate = () => {
    if (!form.sender_email.trim()) return 'Sender email is required.';
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!re.test(form.sender_email.trim())) return 'Sender email is not a valid email address.';
    if (form.reply_to.trim() && !re.test(form.reply_to.trim())) return 'Reply-To is not a valid email address.';
    return null;
  };

  const handleSave = async () => {
    const err = validate();
    if (err) { setMsg(err); return; }
    setSaving(true); setMsg('');
    try {
      const res = await apiClient().put('/api/admin/sender-email', {
        sender_email: form.sender_email.trim(),
        sender_name:  form.sender_name.trim(),
        reply_to:     form.reply_to.trim() || '',
      });
      setData({
        sender_email: res.data.sender_email || '',
        sender_name:  res.data.sender_name  || '',
        reply_to:     res.data.reply_to     || '',
      });
      setEditing(false);
    } catch (e) {
      setMsg(e.response?.data?.error || 'Failed to save.');
    } finally { setSaving(false); }
  };

  const labelStyle = { padding: '0.35rem 0', color: '#6b7280', width: '140px', fontSize: '0.875rem', verticalAlign: 'top' };
  const valueStyle = { padding: '0.35rem 0', color: '#111827', fontWeight: 500, fontSize: '0.875rem' };
  const emptyStyle = { color: '#9ca3af', fontWeight: 400, fontStyle: 'italic' };
  const noteStyle  = { margin: '1rem 0 0', fontSize: '0.8rem', color: '#6b7280' };
  const cancelBtn  = { padding: '0.5rem 1.25rem', background: '#f1f5f9', color: '#374151', border: '1.5px solid #d1d5db', borderRadius: '8px', fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer' };

  if (loading) return null;

  return (
    <div style={{ marginTop: '2rem' }}>
      <h2 style={{ fontSize: '1.05rem', fontWeight: 700, marginBottom: '0.75rem' }}>Sender Email</h2>
      <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: '12px', overflow: 'hidden', padding: '1.25rem' }}>
        {!editing ? (
          <>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '1rem' }}>
              <tbody>
                <tr>
                  <td style={labelStyle}>Sender Name</td>
                  <td style={valueStyle}>{data.sender_name || <span style={emptyStyle}>Not set</span>}</td>
                </tr>
                <tr>
                  <td style={labelStyle}>Sender Email</td>
                  <td style={valueStyle}>{data.sender_email || <span style={emptyStyle}>Not set</span>}</td>
                </tr>
                <tr>
                  <td style={labelStyle}>Reply-To</td>
                  <td style={valueStyle}>{data.reply_to || <span style={emptyStyle}>Same as sender</span>}</td>
                </tr>
              </tbody>
            </table>
            {!isViewer && (
              <button type="button" onClick={openEdit}
                style={{ padding: '0.45rem 1.1rem', background: '#f1f5f9', color: '#374151', border: '1.5px solid #d1d5db', borderRadius: '8px', fontSize: '0.875rem', fontWeight: 600, cursor: 'pointer' }}>
                Edit
              </button>
            )}
            <p style={noteStyle}>
              Sender email must be on a verified SMTP2GO sender domain. Currently verified: <strong>scala.org.au</strong>
            </p>
          </>
        ) : (
          <>
            <div className="form-group">
              <label>Sender Name</label>
              <input
                value={form.sender_name}
                onChange={e => setForm(f => ({ ...f, sender_name: e.target.value }))}
                placeholder="SCALA Membership"
                autoFocus
              />
            </div>
            <div className="form-group">
              <label>Sender Email</label>
              <input
                type="email"
                value={form.sender_email}
                onChange={e => setForm(f => ({ ...f, sender_email: e.target.value }))}
                placeholder="members@scala.org.au"
              />
            </div>
            <div className="form-group">
              <label>
                Reply-To{' '}
                <span style={{ fontWeight: 400, color: '#6b7280', fontSize: '0.85em' }}>
                  (optional — leave blank to use sender email)
                </span>
              </label>
              <input
                type="email"
                value={form.reply_to}
                onChange={e => setForm(f => ({ ...f, reply_to: e.target.value }))}
                placeholder="admin@scala.org.au"
              />
            </div>
            {msg && (
              <p style={{ fontSize: '0.85rem', color: '#dc2626', marginBottom: '0.75rem' }}>{msg}</p>
            )}
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="btn btn-primary"
                style={{ width: 'auto', padding: '0.5rem 1.25rem' }}
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button type="button" onClick={handleCancel} disabled={saving} style={cancelBtn}>
                Cancel
              </button>
            </div>
            <p style={noteStyle}>
              Sender email must be on a verified SMTP2GO sender domain. Currently verified: <strong>scala.org.au</strong>
            </p>
          </>
        )}
      </div>
    </div>
  );
}

// ── Admin Settings Tab ───────────────────────────────────────────────────────
function AdminSettingsTab() {
  const currentUser = getStoredUser();
  const isViewer = currentUser.role === 'viewer';

  const [adminUsers, setAdminUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({ first_name: '', last_name: '', email: '' });
  const [addMsg, setAddMsg] = useState('');
  const [addSaving, setAddSaving] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null); // { id, name }
  const [removing, setRemoving] = useState(false);

  const loadAdminUsers = () => {
    setLoading(true);
    apiClient().get('/api/admin/admin-users')
      .then(res => setAdminUsers(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadAdminUsers(); }, []);

  const handleAddViewer = async (e) => {
    e.preventDefault();
    setAddSaving(true); setAddMsg('');
    try {
      await apiClient().post('/api/admin/admin-users', addForm);
      setAddMsg('Invitation sent.');
      setAddForm({ first_name: '', last_name: '', email: '' });
      loadAdminUsers();
      setTimeout(() => { setShowAddModal(false); setAddMsg(''); }, 1500);
    } catch (err) {
      setAddMsg(err.response?.data?.error || 'Failed to add viewer.');
    } finally { setAddSaving(false); }
  };

  const handleRemove = async () => {
    if (!removeTarget) return;
    setRemoving(true);
    try {
      await apiClient().delete(`/api/admin/admin-users/${removeTarget.id}`);
      setRemoveTarget(null);
      loadAdminUsers();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to remove user.');
    } finally { setRemoving(false); }
  };

  if (loading) return <p style={{ color: '#6b7280' }}>Loading...</p>;

  const admins  = adminUsers.filter(u => u.role === 'tenantadmin');
  const viewers = adminUsers.filter(u => u.role === 'viewer');

  const thStyle = { padding: '0.75rem 1rem', textAlign: 'left', fontWeight: 600, color: '#374151' };
  const tdStyle = (extra = {}) => ({ padding: '0.75rem 1rem', ...extra });

  const renderRow = (u, i, arr, showActions) => {
    const isMe = u.user_id === currentUser.user_id;
    return (
      <tr key={u.user_id} style={{ borderBottom: i < arr.length - 1 ? '1px solid var(--border)' : 'none' }}>
        <td style={tdStyle({ color: '#111827' })}>
          {u.full_name || '—'}
          {isMe && (
            <span style={{
              marginLeft: '0.5rem', background: '#d1fae5', color: '#065f46',
              borderRadius: '9999px', padding: '0.1rem 0.55rem',
              fontSize: '0.7rem', fontWeight: 700, verticalAlign: 'middle',
            }}>You</span>
          )}
        </td>
        <td style={tdStyle({ color: '#374151' })}>{u.email}</td>
        <td style={tdStyle({ color: '#6b7280' })}>
          {u.created_at ? new Date(u.created_at).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
        </td>
        {showActions && (
          <td style={tdStyle()}>
            {!isMe && (
              <button
                type="button"
                onClick={() => setRemoveTarget({ id: u.user_id, name: u.full_name || u.email })}
                style={{
                  padding: '0.3rem 0.8rem', background: '#fee2e2', color: '#dc2626',
                  border: '1px solid #fca5a5', borderRadius: '6px',
                  fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer',
                }}
              >
                Remove
              </button>
            )}
          </td>
        )}
      </tr>
    );
  };

  return (
    <div style={{ maxWidth: '760px' }}>

      {/* ── Administrators section ── */}
      <h2 style={{ fontSize: '1.05rem', fontWeight: 700, marginBottom: '0.75rem' }}>Administrators</h2>
      <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: '12px', overflow: 'hidden', marginBottom: '2rem' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
          <thead>
            <tr style={{ background: '#f8fafc', borderBottom: '1px solid var(--border)' }}>
              <th style={thStyle}>Name</th>
              <th style={thStyle}>Email</th>
              <th style={thStyle}>Date Added</th>
            </tr>
          </thead>
          <tbody>
            {admins.map((u, i) => renderRow(u, i, admins, false))}
          </tbody>
        </table>
      </div>

      {/* ── Viewers section ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
        <h2 style={{ fontSize: '1.05rem', fontWeight: 700, margin: 0 }}>Viewers</h2>
        {!isViewer && (
          <button
            className="btn btn-primary"
            style={{ width: 'auto', padding: '0.5rem 1.25rem' }}
            onClick={() => { setShowAddModal(true); setAddMsg(''); setAddForm({ first_name: '', last_name: '', email: '' }); }}
          >
            + Add Viewer
          </button>
        )}
      </div>
      <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: '12px', overflow: 'hidden' }}>
        {viewers.length === 0 ? (
          <p style={{ padding: '1.5rem 1rem', margin: 0, textAlign: 'center', color: '#9ca3af', fontSize: '0.875rem' }}>
            No viewers added yet.
          </p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '1px solid var(--border)' }}>
                <th style={thStyle}>Name</th>
                <th style={thStyle}>Email</th>
                <th style={thStyle}>Date Added</th>
                {!isViewer && <th style={thStyle}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {viewers.map((u, i) => renderRow(u, i, viewers, !isViewer))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Sender Email section ── */}
      <SenderEmailCard />

      {/* Add Viewer Modal */}
      {showAddModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '2rem', width: '100%', maxWidth: '440px', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
            <h3 style={{ fontWeight: 700, marginBottom: '1.25rem', fontSize: '1.05rem' }}>Add Viewer</h3>
            <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '1rem' }}>
              Viewers can see all member data but cannot make any changes. An invitation email will be sent with a link to set their password.
            </p>
            <form onSubmit={handleAddViewer}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label>First Name</label>
                  <input
                    value={addForm.first_name}
                    onChange={e => setAddForm(f => ({ ...f, first_name: e.target.value }))}
                    required
                    autoFocus
                  />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label>Last Name</label>
                  <input
                    value={addForm.last_name}
                    onChange={e => setAddForm(f => ({ ...f, last_name: e.target.value }))}
                    required
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Email Address</label>
                <input
                  type="email"
                  value={addForm.email}
                  onChange={e => setAddForm(f => ({ ...f, email: e.target.value }))}
                  required
                  placeholder="viewer@example.com"
                />
              </div>
              {addMsg && (
                <p style={{ fontSize: '0.85rem', color: addMsg === 'Invitation sent.' ? '#15803d' : '#dc2626', marginBottom: '0.75rem' }}>{addMsg}</p>
              )}
              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
                <button
                  type="button"
                  onClick={() => { setShowAddModal(false); setAddMsg(''); }}
                  style={{ padding: '0.5rem 1.25rem', background: '#f1f5f9', color: '#374151', border: '1.5px solid #d1d5db', borderRadius: '8px', fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={addSaving}
                  style={{ width: 'auto', padding: '0.5rem 1.25rem' }}
                >
                  {addSaving ? 'Sending...' : 'Send Invitation'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Remove Confirmation Modal */}
      {removeTarget && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '2rem', width: '100%', maxWidth: '400px', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
            <h3 style={{ fontWeight: 700, marginBottom: '0.75rem', fontSize: '1.05rem' }}>Remove Viewer</h3>
            <p style={{ fontSize: '0.9rem', color: '#374151', marginBottom: '1.5rem' }}>
              Remove <strong>{removeTarget.name}</strong> as a viewer? They will no longer be able to log in.
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setRemoveTarget(null)}
                disabled={removing}
                style={{ padding: '0.5rem 1.25rem', background: '#f1f5f9', color: '#374151', border: '1.5px solid #d1d5db', borderRadius: '8px', fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleRemove}
                disabled={removing}
                style={{ padding: '0.5rem 1.25rem', background: '#dc2626', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 600, fontSize: '0.875rem', cursor: removing ? 'not-allowed' : 'pointer', opacity: removing ? 0.7 : 1 }}
              >
                {removing ? 'Removing...' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Shared Components ────────────────────────────────────────────────────────
function StatCard({ label, value }) {
  return (
    <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: '12px', padding: '1.25rem' }}>
      <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
      <div style={{ fontSize: '1.75rem', fontWeight: 700, color: 'var(--primary)' }}>{value}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.cancelled;
  return (
    <span style={{ background: s.bg, color: s.color, padding: '0.2rem 0.6rem', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
      {status ? status.charAt(0).toUpperCase() + status.slice(1) : '—'}
    </span>
  );
}

// ── Admin Actions Tab ────────────────────────────────────────────────────────
// Home for one-off administrative jobs that are not a list or a setting. The
// first is "Add a member" — enrolling someone who paid outside Stripe (cash at
// the door, bank transfer, complimentary). More actions land here as cards.
function AdminActionsTab() {
  const isViewer = getStoredUser().role === 'viewer';

  return (
    <div style={{ maxWidth: '760px' }}>
      {isViewer && (
        <p style={{ fontSize: '0.875rem', color: '#92400e', background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: '8px', padding: '0.75rem 1rem', marginBottom: '1.5rem' }}>
          Viewers have read-only access. These actions are available to administrators only.
        </p>
      )}
      <AddMemberCard disabled={isViewer} />
    </div>
  );
}

const SELECT_STYLE = { width: '100%', padding: '0.75rem 1rem', border: '1.5px solid var(--border)', borderRadius: '8px', fontSize: '0.95rem', background: '#fff' };

const EMPTY_MEMBER_FORM = {
  first_name: '', last_name: '', email: '', phone: '', address: '',
  type_id: '', start_date: '', payment_type: 'manual',
};

function AddMemberCard({ disabled }) {
  const [types, setTypes] = useState([]);
  const [typesLoading, setTypesLoading] = useState(true);
  const [form, setForm] = useState(EMPTY_MEMBER_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  useEffect(() => {
    apiClient().get('/api/admin/membership-types')
      .then(res => setTypes(res.data.filter(t => t.is_active)))
      .catch(console.error)
      .finally(() => setTypesLoading(false));
  }, []);

  const selectedType = types.find(t => String(t.type_id) === String(form.type_id));
  const fee = selectedType ? parseFloat(selectedType.fee) || 0 : 0;
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.type_id) { setError('Choose a membership type.'); return; }
    setSaving(true); setError(''); setResult(null);
    try {
      const payload = {
        full_name: `${form.first_name.trim()} ${form.last_name.trim()}`.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        address: form.address.trim() || null,
        type_id: form.type_id,
        start_date: form.start_date || undefined,
        payment_type: fee > 0 ? form.payment_type : 'none',
      };
      const res = await apiClient().post('/api/admin/members', payload);
      setResult(res.data);
      setForm(EMPTY_MEMBER_FORM);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add the member.');
    } finally { setSaving(false); }
  };

  const fmtDate = (d) => d
    ? new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })
    : 'Lifetime';

  return (
    <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: '12px', padding: '1.5rem', marginBottom: '2rem' }}>
      <h2 style={{ fontSize: '1.05rem', fontWeight: 700, margin: '0 0 0.35rem' }}>Add a member</h2>
      <p style={{ fontSize: '0.85rem', color: '#6b7280', margin: '0 0 1.25rem' }}>
        For someone who has paid in cash or by bank transfer, or is being given a membership.
        They receive the welcome email, with a link to set their password if they are new to the portal.
        Someone who already holds a membership is renewed from the Members tab instead.
      </p>

      {result && (
        <div style={{ background: '#dcfce7', border: '1px solid #86efac', borderRadius: '8px', padding: '0.9rem 1rem', marginBottom: '1.25rem', fontSize: '0.875rem', color: '#14532d' }}>
          <div style={{ fontWeight: 700, marginBottom: '0.35rem' }}>
            {result.user_full_name} added as {result.membership_number}
          </div>
          <div>{result.membership_type} · valid until {fmtDate(result.expiry_date)}</div>
          <div>
            {result.payment_recorded > 0
              ? `Manual payment of ${Number(result.payment_recorded).toFixed(2)} recorded.`
              : 'No payment recorded.'}
          </div>
          <div style={{ marginTop: '0.35rem', color: result.welcome_sent ? '#14532d' : '#991b1b', fontWeight: result.welcome_sent ? 400 : 600 }}>
            {result.welcome_sent
              ? `Welcome email sent to ${result.user_email}${result.new_account ? ' with a set-password link (valid 7 days).' : '.'}`
              : `Welcome email was NOT sent${result.welcome_error ? ` — ${result.welcome_error}` : ''}. The membership stands; contact them directly.`}
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <fieldset disabled={disabled || saving} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <div className="form-group">
              <label>First name</label>
              <input value={form.first_name} onChange={set('first_name')} required autoComplete="off" />
            </div>
            <div className="form-group">
              <label>Last name</label>
              <input value={form.last_name} onChange={set('last_name')} required autoComplete="off" />
            </div>
          </div>
          <div className="form-group">
            <label>Email address</label>
            <input type="email" value={form.email} onChange={set('email')} required autoComplete="off" placeholder="member@example.com" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '0.75rem' }}>
            <div className="form-group">
              <label>Phone <span style={{ fontWeight: 400, color: '#6b7280', fontSize: '0.85em' }}>(optional)</span></label>
              <input value={form.phone} onChange={set('phone')} autoComplete="off" />
            </div>
            <div className="form-group">
              <label>Address <span style={{ fontWeight: 400, color: '#6b7280', fontSize: '0.85em' }}>(optional)</span></label>
              <input value={form.address} onChange={set('address')} autoComplete="off" />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '0.75rem' }}>
            <div className="form-group">
              <label>Membership type</label>
              <select value={form.type_id} onChange={set('type_id')} required style={SELECT_STYLE}>
                <option value="">{typesLoading ? 'Loading…' : 'Choose a type…'}</option>
                {types.map(t => (
                  <option key={t.type_id} value={t.type_id}>
                    {t.name} — {parseFloat(t.fee) > 0 ? `${parseFloat(t.fee).toFixed(2)}` : 'free'} · {fmtDuration(t.duration_months, t.duration_unit)}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Start date <span style={{ fontWeight: 400, color: '#6b7280', fontSize: '0.85em' }}>(blank = today)</span></label>
              <input type="date" value={form.start_date} onChange={set('start_date')} />
            </div>
          </div>
          <div className="form-group">
            <label>Payment</label>
            {fee > 0 ? (
              <select value={form.payment_type} onChange={set('payment_type')} style={SELECT_STYLE}>
                <option value="manual">Paid outside Stripe (cash / bank transfer) — record ${fee.toFixed(2)} as received</option>
                <option value="none">No payment — complimentary membership</option>
              </select>
            ) : (
              <p style={{ margin: 0, fontSize: '0.85rem', color: '#6b7280' }}>
                {selectedType ? 'This membership type has no fee, so no payment is recorded.' : 'Choose a membership type first.'}
              </p>
            )}
          </div>

          {error && (
            <p style={{ fontSize: '0.85rem', color: '#dc2626', marginBottom: '0.75rem' }}>{error}</p>
          )}

          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', marginTop: '0.5rem' }}>
            <button type="submit" className="btn btn-primary" style={{ width: 'auto', padding: '0.5rem 1.25rem' }}>
              {saving ? 'Adding…' : 'Add member & send welcome email'}
            </button>
            <button
              type="button"
              onClick={() => { setForm(EMPTY_MEMBER_FORM); setError(''); setResult(null); }}
              style={{ padding: '0.5rem 1.25rem', background: '#f1f5f9', color: '#374151', border: '1.5px solid #d1d5db', borderRadius: '8px', fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer' }}
            >
              Clear
            </button>
          </div>
        </fieldset>
      </form>
    </div>
  );
}

// ── Main Admin Dashboard ─────────────────────────────────────────────────────
const TABS = [
  { key: 'overview',        label: 'Overview' },
  { key: 'members',         label: 'Members' },
  { key: 'renewals',        label: 'Renewals' },
  { key: 'lapsed',          label: 'Lapsed' },
  { key: 'types',           label: 'Membership Types' },
  { key: 'admin-actions',   label: 'Admin Actions' },
  // "Email" to match the equivalent tab in the FOOM and Performer apps. The key
  // stays 'emails' - it is the URL fragment and referenced below, and renaming it
  // would break existing bookmarks for no gain.
  { key: 'emails',          label: 'Email' },
  { key: 'settings',        label: 'Settings' },
  { key: 'admin-settings',  label: 'Admin Settings' },
];

function AdminDashboard() {
  const [activeTab, setActiveTab] = useState('overview');
  const user = getStoredUser();
  const isViewer = user.role === 'viewer';

  const handleLogout = () => { localStorage.clear(); window.location.href = '/login'; };

  return (
    <div className="dashboard">
      <div className="admin-navbar">
        <div className="admin-navbar-brand"><span style={{ color: '#1a1a2e', fontWeight: 'bold' }}>Me</span><span style={{ color: '#d4af37', fontWeight: 'bold' }}>Member</span></div>
        <div className="admin-navbar-user">
          {/* Only an admin who is also a paying member sees this — their own
              membership lives behind the member pages, which the admin role
              alone cannot reach. Nobody else's banner changes. */}
          {user.has_membership && (
            <a className="m-viewswitch" href="/dashboard" title="View and renew your own membership">
              Member view
            </a>
          )}
          <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            {user.full_name}
            <span style={{
              background: isViewer ? '#3b82f6' : '#1e3a5f',
              color: '#fff', borderRadius: '4px', padding: '2px 8px',
              fontSize: '0.7rem', fontWeight: 600,
            }}>
              {isViewer ? 'Viewer' : 'Admin'}
            </span>
          </span>
          <button className="m-signout" onClick={handleLogout}>Sign out</button>
        </div>
      </div>

      <nav className="admin-tab-bar">
        {TABS.map(t => (
          <a
            key={t.key}
            href={`#${t.key}`}
            className={activeTab === t.key ? 'active' : ''}
            onClick={e => { e.preventDefault(); setActiveTab(t.key); }}
          >
            {t.label}
          </a>
        ))}
      </nav>

      <main className="main-content">
        {activeTab === 'overview'       && <OverviewTab />}
        {activeTab === 'members'        && <MembersTab />}
        {activeTab === 'renewals'       && <RenewalsTab />}
        {activeTab === 'lapsed'         && <LapsedTab />}
        {activeTab === 'types'          && <MembershipTypesTab />}
        {activeTab === 'admin-actions'  && <AdminActionsTab />}
        {activeTab === 'emails'         && <EmailsTab />}
        {activeTab === 'settings'       && <SettingsTab />}
        {activeTab === 'admin-settings' && <AdminSettingsTab />}
      </main>
    </div>
  );
}

export default AdminDashboard;
